import DOMPurify from "dompurify";
import moment from "moment";
import { Tooltip } from "@fluentui/react-components";
import { dateOnlyFormatted } from "../utils/systemSettings";
import { parsePhoneNumberFromString } from 'libphonenumber-js';
import {
  CompletedTaskIcon,
  InProgressTaskIcon,
  SlippingTaskIcon,
  MonitoringTaskIcon,
  OnHoldTaskIcon
} from "../views/dashboard/icons/icons";
export const getHomePage = (type) => {
  return "/dashboard";
};

export const meetingRepeatTypes = [
  { name: "Does not repeat", value: "Does not repeat" },
  { name: "Every Weekday", value: "Every Weekday" },
  { name: "Daily", value: "Daily" },
  { name: "Weekly", value: "Weekly" },
  { name: "Monthly", value: "Monthly" },
  { name: "Yearly", value: "Yearly" },
];

export const getWeekRange = (dateString) => {
  // Parse the input date string to extract year, month, and date
  const year = parseInt(dateString.substring(0, 2), 10) + 2000; // Assuming the year is in YY format
  const month = parseInt(dateString.substring(2, 4), 10) - 1; // Adjust month to zero-based index
  const date = parseInt(dateString.substring(4), 10);

  // Create a new date object
  const currentDate = new Date(year, month, date);

  // Calculate the start date of the week (Monday)
  const startDate = new Date(currentDate);
  const currentDayOfWeek = startDate.getDay(); // Get the current day of the week (0 = Sunday, 1 = Monday, ..., 6 = Saturday)
  const diff = currentDayOfWeek === 0 ? -6 : 1 - currentDayOfWeek; // Calculate the difference in days to Monday
  startDate.setDate(startDate.getDate() + diff); // Adjust to Monday

  // Calculate the end date of the week (Friday)
  const endDate = new Date(startDate);
  endDate.setDate(endDate.getDate() + 4); // Adjust to Friday

  // Format the dates to 'MMM DD, YYYY' format
  const options = { month: "short", day: "2-digit", year: "numeric" };
  const formattedStartDate = startDate.toLocaleDateString(undefined, options);
  const formattedEndDate = endDate.toLocaleDateString(undefined, options);

  return { startDate: formattedStartDate, endDate: formattedEndDate };
};

export const getCurrentMonthYear = () => {
  const monthNames = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
  ];
  const date = new Date();
  const month = monthNames[date.getMonth()];
  const year = date.getFullYear();
  return `${month} ${year}`;
};

export const getCurrentDate = (date) => {
  const currentDate = new Date(date);
  // Get month, day, and year
  const month = currentDate.getMonth() + 1; // Month is zero-based, so we add 1
  const day = currentDate.getDate();
  const year = currentDate.getFullYear();

  // Format month and day with leading zeros if needed
  const formattedMonth = month < 10 ? "0" + month : month;
  const formattedDay = day < 10 ? "0" + day : day;

  // Concatenate month, day, and year with '/' separator
  const formattedDate = formattedMonth + "/" + formattedDay + "/" + year;

  return formattedDate;
};

export const workingHours = [
  { name: "01:00", value: "01:00" },
  { name: "02:00", value: "02:00" },
  { name: "03:00", value: "03:00" },
  { name: "04:00", value: "04:00" },
  { name: "05:00", value: "05:00" },
  { name: "06:00", value: "06:00" },
  { name: "07:00", value: "07:00" },
  { name: "08:00", value: "08:00" },
  { name: "09:00", value: "09:00" },
  { name: "10:00", value: "10:00" },
  { name: "11:00", value: "11:00" },
  { name: "12:00", value: "12:00" },
  { name: "13:00", value: "13:00" },
  { name: "14:00", value: "14:00" },
  { name: "15:00", value: "15:00" },
  { name: "16:00", value: "16:00" },
  { name: "17:00", value: "17:00" },
  { name: "18:00", value: "18:00" },
  { name: "19:00", value: "19:00" },
  { name: "20:00", value: "20:00" },
  { name: "21:00", value: "21:00" },
  { name: "22:00", value: "22:00" },
  { name: "23:00", value: "23:00" },
  { name: "24:00", value: "24:00" },
];

export const days = [
  { name: "Monday", value: "Monday" },
  { name: "Tuesday", value: "Tuesday" },
  { name: "Wednesday", value: "Wednesday" },
  { name: "Thursday", value: "Thursday" },
  { name: "Friday", value: "Friday" },
  { name: "Saturday", value: "Saturday" },
  { name: "Sunday", value: "Sunday" },
];

export const generateRandomString = (length = 12) => {
  const specialChars = "!@#$%^&*()-_=+[]{}|;:,.<>?";
  const numbers = "0123456789";
  const upperCaseChars = "ABCDEFGHIJKLMNOPQRSTUVWXYZ";
  const lowerCaseChars = "abcdefghijklmnopqrstuvwxyz";

  // Ensure minimum length of 8
  const finalLength = Math.max(8, length);

  // Include at least one character from each required set
  const requiredChars = [
    specialChars[Math.floor(Math.random() * specialChars.length)],
    numbers[Math.floor(Math.random() * numbers.length)],
    upperCaseChars[Math.floor(Math.random() * upperCaseChars.length)],
    lowerCaseChars[Math.floor(Math.random() * lowerCaseChars.length)]
  ];

  // Generate remaining characters to meet the required length
  const allChars = specialChars + numbers + upperCaseChars + lowerCaseChars;
  const remainingLength = finalLength - requiredChars.length;

  for (let i = 0; i < remainingLength; i++) {
    requiredChars.push(allChars[Math.floor(Math.random() * allChars.length)]);
  }

  // Shuffle the array using Fisher-Yates algorithm for better randomization
  for (let i = requiredChars.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [requiredChars[i], requiredChars[j]] = [requiredChars[j], requiredChars[i]];
  }

  return requiredChars.join("");
};

export const decimalSeperator = [
  { name: "12.00", value: "." },
  { name: "12,00", value: "," },
];

export const groupSeperator = [
  { name: "12 345", value: " " },
  { name: "12,345", value: "," },
  { name: "12.345", value: "." },
  { name: "12'345", value: "'" },
];

const currentTime = new Date();
// Custom format: HH:MM (24-hour)
const timeFormat1 = `${String(currentTime.getHours()).padStart(
  2,
  "0"
)}:${String(currentTime.getMinutes()).padStart(2, "0")}`;
// Custom format: HH:MM AM/PM (12-hour)
const hours12 = currentTime.getHours() % 12 || 12; // Convert to 12-hour format
const ampm = currentTime.getHours() < 12 ? "AM" : "PM";
const timeFormat2 = `${String(hours12).padStart(2, "0")}:${String(
  currentTime.getMinutes()
).padStart(2, "0")} ${ampm}`;

export const timeFormats = [
  { name: timeFormat1, value: "hh:mm" },
  { name: timeFormat2, value: "hh:mm a" },
];

export function formatDate(date, pattern) {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  const shortYear = String(year).slice(-2);
  const monthNames = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
  ];
  const monthAbbr = monthNames[date.getMonth()].slice(0, 3);
  const monthFull = monthNames[date.getMonth()];

  switch (pattern) {
    case "dd/MM/yyyy":
      return `${day}/${month}/${year}`;
    case "MM/dd/yyyy":
      return `${month}/${day}/${year}`;
    case "yyyy/MM/dd":
      return `${year}/${month}/${day}`;
    case "yy/MM/dd":
      return `${shortYear}/${month}/${day}`;
    case "yyyy-MM-dd":
      return `${year}-${month}-${day}`;
    case "yy-MM-dd":
      return `${shortYear}-${month}-${day}`;
    case "dd-MM-yyyy":
      return `${day}-${month}-${year}`;
    case "MM-dd-yyyy":
      return `${month}-${day}-${year}`;
    case "yyyy-MM-dd":
      return `${year}-${month}-${day}`;
    case "dd/MM/yy":
      return `${day}/${month}/${shortYear}`;
    case "MM/dd/yy":
      return `${month}/${day}/${shortYear}`;
    case "dd MMM yyyy":
      return `${day} ${monthAbbr} ${year}`;
    case "dd, MMM yyyy":
      return `${day}, ${monthAbbr} ${year}`;
    case "dd MMM, yyyy":
      return `${day} ${monthAbbr}, ${year}`;
    case "dd-MMM-yyyy":
      return `${day}-${monthAbbr}-${year}`;
    case "dd MMMM yyyy":
      return `${day} ${monthFull} ${year}`;
    default:
      return "";
  }
}

const patterns = [
  { pattern: "dd/MM/yyyy" },
  { pattern: "MM/dd/yyyy" },
  { pattern: "yyyy/MM/dd" },
  { pattern: "yy/MM/dd" },
  { pattern: "yyyy-MM-dd" },
  { pattern: "yy-MM-dd" },
  { pattern: "dd/MM/yy" },
  { pattern: "MM/dd/yy" },
  { pattern: "dd MMM yyyy" },
  { pattern: "dd, MMM yyyy" },
  { pattern: "dd MMM, yyyy" },
  { pattern: "dd-MMM-yyyy" },
  { pattern: "dd MMMM yyyy" },
];

const currentDate = new Date();

export const dateFormats = patterns.map(({ pattern }) => ({
  name: formatDate(currentDate, pattern),
  value: pattern,
}));

export const countries = [
  { name: "Albania", code: "AL", value: "Albania" },
  { name: "Åland Islands", code: "AX", value: "Åland Islands" },
  { name: "Algeria", code: "DZ", value: "Algeria" },
  { name: "American Samoa", code: "AS", value: "American Samoa" },
  { name: "Andorra", code: "AD", value: "Andorra" },
  { name: "Angola", code: "AO", value: "Angola" },
  { name: "Anguilla", code: "AI", value: "Anguilla" },
  { name: "Antarctica", code: "AQ", value: "Antarctica" },
  { name: "Antigua and Barbuda", code: "AG", value: "Antigua and Barbuda" },
  { name: "Argentina", code: "AR", value: "Argentina" },
  { name: "Armenia", code: "AM", value: "Armenia" },
  { name: "Aruba", code: "AW", value: "Aruba" },
  {
    name: "Australia",
    code: "AU",
    value: "Australia",
  },
  {
    name: "Austria",
    code: "AT",
    value: "Austria",
  },
  {
    name: "Azerbaijan",
    code: "AZ",
    value: "Azerbaijan",
  },
  {
    name: "Bahamas",
    code: "BS",
    value: "Bahamas",
  },
  {
    name: "Bahrain",
    code: "BH",
    value: "Bahrain",
  },
  {
    name: "Bangladesh",
    code: "BD",
    value: "Bangladesh",
  },
  {
    name: "Barbados",
    code: "BB",
    value: "Barbados",
  },
  {
    name: "Belarus",
    code: "BY",
    value: "Belarus",
  },
  {
    name: "Belgium",
    code: "BE",
    value: "Belgium",
  },
  {
    name: "Belize",
    code: "BZ",
    value: "Belize",
  },
  {
    name: "Benin",
    code: "BJ",
    value: "Benin",
  },
  {
    name: "Bermuda",
    code: "BM",
    value: "Bermuda",
  },
  {
    name: "Bhutan",
    code: "BT",
    value: "Bhutan",
  },
  {
    name: "Bolivia (Plurinational State of)",
    code: "BO",
    value: "Bolivia (Plurinational State of)",
  },
  {
    name: "Bonaire, Sint Eustatius and Saba",
    code: "BQ",
    value: "Bonaire, Sint Eustatius and Saba",
  },
  {
    name: "Bosnia and Herzegovina",
    code: "BA",
    value: "Bosnia and Herzegovina",
  },
  {
    name: "Botswana",
    code: "BW",
    value: "Botswana",
  },
  {
    name: "Bouvet Island",
    code: "BV",
    value: "Bouvet Island",
  },
  {
    name: "Brazil",
    code: "BR",
    value: "Brazil",
  },
  {
    name: "British Indian Ocean Territory",
    code: "IO",
    value: "British Indian Ocean Territory",
  },
  {
    name: "Brunei Darussalam",
    code: "BN",
    value: "Brunei Darussalam",
  },
  {
    name: "Bulgaria",
    code: "BG",
    value: "Bulgaria",
  },
  {
    name: "Burkina Faso",
    code: "BF",
    value: "Burkina Faso",
  },
  {
    name: "Burundi",
    code: "BI",
    value: "Burundi",
  },
  {
    name: "Cabo Verde",
    code: "CV",
    value: "Cabo Verde",
  },
  {
    name: "Cambodia",
    code: "KH",
    value: "Cambodia",
  },
  {
    name: "Cameroon",
    code: "CM",
    value: "Cameroon",
  },
  {
    name: "Canada",
    code: "CA",
    value: "Canada",
  },
  {
    name: "Cayman Islands",
    code: "KY",
    value: "Cayman Islands",
  },
  {
    name: "Central African Republic",
    code: "CF",
    value: "Central African Republic",
  },
  {
    name: "Chad",
    code: "TD",
    value: "Chad",
  },
  {
    name: "Chile",
    code: "CL",
    value: "Chile",
  },
  {
    name: "China",
    code: "CN",
    value: "China",
  },
  {
    name: "Christmas Island",
    code: "CX",
    value: "Christmas Island",
  },
  {
    name: "Cocos (Keeling) Islands",
    code: "CC",
    value: "Cocos (Keeling) Islands",
  },
  {
    name: "Colombia",
    code: "CO",
    value: "Colombia",
  },
  {
    name: "Comoros",
    code: "KM",
    value: "Comoros",
  },
  {
    name: "Congo",
    code: "CD",
    value: "Congo",
  },
  {
    name: "Congo",
    code: "CG",
    value: "Congo",
  },
  { name: "Cook Islands", value: "Cook Islands", code: "CK" },
  {
    name: "Costa Rica",
    code: "CR",
    value: "Costa Rica",
  },
  {
    name: "Croatia",
    code: "HR",
    value: "Croatia",
  },
  {
    name: "Cuba",
    code: "CU",
    value: "Cuba",
  },
  {
    name: "Curaçao",
    code: "CW",
    value: "Curaçao",
  },
  {
    name: "Cyprus",
    code: "CY",
    value: "Cyprus",
  },
  {
    name: "Czechia",
    code: "CZ",
    value: "Czechia",
  },
  {
    name: "Côte d'Ivoire",
    code: "CI",
    value: "Côte d'Ivoire",
  },
  {
    name: "Denmark",
    code: "DK",
    value: "Denmark",
  },
  {
    name: "Djibouti",
    code: "DJ",
    value: "Djibouti",
  },
  {
    name: "Dominica",
    code: "DM",
    value: "Dominica",
  },
  {
    name: "Dominican Republic",
    code: "DO",
    value: "Dominican Republic",
  },
  {
    name: "Ecuador",
    code: "EC",
    value: "Ecuador",
  },
  {
    name: "Egypt",
    code: "EG",
    value: "Egypt",
  },
  {
    name: "El Salvador",
    code: "SV",
    value: "El Salvador",
  },
  {
    name: "Equatorial Guinea",
    code: "GQ",
    value: "Equatorial Guinea",
  },
  {
    name: "Eritrea",
    code: "ER",
    value: "Eritrea",
  },
  {
    name: "Estonia",
    code: "EE",
    value: "Estonia",
  },
  {
    name: "Eswatini",
    code: "SZ",
    value: "Eswatini",
  },
  {
    name: "Ethiopia",
    code: "ET",
    value: "Ethiopia",
  },
  {
    name: "Falkland Islands [Malvinas]",
    code: "FK",
    value: "Falkland Islands [Malvinas]",
  },
  {
    name: "Faroe Islands",
    code: "FO",
    value: "Faroe Islands",
  },
  {
    name: "Fiji",
    code: "FJ",
    value: "Fiji",
  },
  {
    name: "Finland",
    code: "FI",
    value: "Finland",
  },
  {
    name: "France",
    code: "FR",
    value: "France",
  },
  {
    name: "French Guiana",
    code: "GF",
    value: "French Guiana",
  },
  {
    name: "French Polynesia",
    code: "PF",
    value: "French Polynesia",
  },
  {
    name: "French Southern Territories",
    code: "TF",
    value: "French Southern Territories",
  },
  {
    name: "Gabon",
    code: "GA",
    value: "Gabon",
  },
  {
    name: "Gambia",
    code: "GM",
    value: "Gambia",
  },
  {
    name: "Georgia",
    code: "GE",
    value: "Georgia",
  },
  {
    name: "Germany",
    code: "DE",
    value: "Germany",
  },
  {
    name: "Ghana",
    code: "GH",
    value: "Ghana",
  },
  {
    name: "Gibraltar",
    code: "GI",
    value: "Gibraltar",
  },
  {
    name: "Greece",
    code: "GR",
    value: "Greece",
  },
  {
    name: "Greenland",
    code: "GL",
    value: "Greenland",
  },
  {
    name: "Grenada",
    code: "GD",
    value: "Grenada",
  },
  { name: "Guadeloupe", value: "Guadeloupe", code: "GP" },
  {
    name: "Guam",
    code: "GU",
    value: "Guam",
  },
  {
    name: "Guatemala",
    code: "GT",
    value: "Guatemala",
  },
  {
    name: "Guernsey",
    code: "GG",
    value: "Guernsey",
  },
  {
    name: "Guinea",
    code: "GN",
    value: "Guinea",
  },
  {
    name: "Guinea-Bissau",
    code: "GW",
    value: "Guinea-Bissau",
  },
  {
    name: "Guyana",
    code: "GY",
    value: "Guyana",
  },
  {
    name: "Haiti",
    code: "HT",
    value: "Haiti",
  },
  {
    name: "Heard Island and McDonald Islands",
    code: "HM",
    value: "Heard Island and McDonald Islands",
  },
  {
    name: "Holy See",
    code: "VA",
    value: "Holy See",
  },
  {
    name: "Honduras",
    code: "HN",
    value: "Honduras",
  },
  {
    name: "Hong Kong",
    code: "HK",
    value: "Hong Kong",
  },
  {
    name: "Hungary",
    code: "HU",
    value: "Hungary",
  },
  {
    name: "Iceland",
    code: "IS",
    value: "Iceland",
  },
  {
    name: "India",
    code: "IN",
    value: "India",
  },
  {
    name: "Indonesia",
    code: "ID",
    value: "Indonesia",
  },
  {
    name: "Iran (Islamic Republic of)",
    code: "IR",
    value: "Iran (Islamic Republic of)",
  },
  {
    name: "Iraq",
    code: "IQ",
    value: "Iraq",
  },
  {
    name: "Ireland",
    code: "IE",
    value: "Ireland",
  },
  {
    name: "Isle of Man",
    code: "IM",
    value: "Isle of Man",
  },
  {
    name: "Israel",
    code: "IL",
    value: "Israel",
  },
  {
    name: "Italy",
    code: "IT",
    value: "Italy",
  },
  {
    name: "Jamaica",
    code: "JM",
    value: "Jamaica",
  },
  {
    name: "Japan",
    code: "JP",
    value: "Japan",
  },
  {
    name: "Jersey",
    code: "JE",
    value: "Jersey",
  },
  {
    name: "Jordan",
    code: "JO",
    value: "Jordan",
  },
  {
    name: "Kazakhstan",
    code: "KZ",
    value: "Kazakhstan",
  },
  {
    name: "Kenya",
    code: "KE",
    value: "Kenya",
  },
  {
    name: "Kiribati",
    code: "KI",
    value: "Kiribati",
  },
  {
    name: "Korea",
    code: "KR",
    value: "Korea",
  },
  {
    name: "Kuwait",
    code: "KW",
    value: "Kuwait",
  },
  {
    name: "Kyrgyzstan",
    code: "KG",
    value: "Kyrgyzstan",
  },
  {
    name: "Lao People's Democratic Republic",
    code: "LA",
    value: "Lao People's Democratic Republic",
  },
  {
    name: "Latvia",
    code: "LV",
    value: "Latvia",
  },
  {
    name: "Lebanon",
    code: "LB",
    value: "Lebanon",
  },
  {
    name: "Lesotho",
    code: "LS",
    value: "Lesotho",
  },
  {
    name: "Liberia",
    code: "LR",
    value: "Liberia",
  },
  {
    name: "Libya",
    code: "LY",
    value: "Libya",
  },
  {
    name: "Liechtenstein",
    code: "LI",
    value: "Liechtenstein",
  },
  {
    name: "Lithuania",
    code: "LT",
    value: "Lithuania",
  },
  {
    name: "Luxembourg",
    code: "LU",
    value: "Luxembourg",
  },
  {
    name: "Macao",
    code: "MO",
    value: "Macao",
  },
  {
    name: "Madagascar",
    code: "MG",
    value: "Madagascar",
  },
  {
    name: "Malawi",
    code: "MW",
    value: "Malawi",
  },
  {
    name: "Malaysia",
    code: "MY",
    value: "Malaysia",
  },
  {
    name: "Maldives",
    code: "MV",
    value: "Maldives",
  },
  {
    name: "Mali",
    code: "ML",
    value: "Mali",
  },
  {
    name: "Malta",
    code: "MT",
    value: "Malta",
  },
  {
    name: "Marshall Islands",
    code: "MH",
    value: "Marshall Islands",
  },
  {
    name: "Martinique",
    code: "MQ",
    value: "Martinique",
  },
  {
    name: "Mauritania",
    code: "MR",
    value: "Mauritania",
  },
  {
    name: "Mauritius",
    code: "MU",
    value: "Mauritius",
  },
  {
    name: "Mayotte",
    code: "YT",
    value: "Mayotte",
  },
  {
    name: "Mexico",
    code: "MX",
    value: "Mexico",
  },
  {
    name: "Micronesia (Federated States of)",
    value: "Micronesia (Federated States of)",
    code: "FM",
  },
  {
    name: "Moldova (Republic of)",
    code: "MD",
    value: "Moldova (Republic of)",
  },
  {
    name: "Monaco",
    code: "MC",
    value: "Monaco",
  },
  {
    name: "Mongolia",
    code: "MN",
    value: "Mongolia",
  },
  {
    name: "Montenegro",
    code: "ME",
    value: "Montenegro",
  },
  {
    name: "Montserrat",
    code: "MS",
    value: "Montserrat",
  },
  {
    name: "Morocco",
    code: "MA",
    value: "Morocco",
  },
  {
    name: "Mozambique",
    code: "MZ",
    value: "Mozambique",
  },
  {
    name: "Myanmar",
    code: "MM",
    value: "Myanmar",
  },
  {
    name: "Namibia",
    code: "NA",
    value: "Namibia",
  },
  {
    name: "Nauru",
    code: "NR",
    value: "Nauru",
  },
  {
    name: "Nepal",
    code: "NP",
    value: "Nepal",
  },
  {
    name: "Netherlands",
    code: "NL",
    value: "Netherlands",
  },
  {
    name: "New Caledonia",
    code: "NC",
    value: "New Caledonia",
  },
  {
    name: "New Zealand",
    code: "NZ",
    value: "New Zealand",
  },
  {
    name: "Nicaragua",
    code: "NI",
    value: "Nicaragua",
  },
  {
    name: "Niger",
    code: "NE",
    value: "Niger",
  },
  {
    name: "Nigeria",
    code: "NG",
    value: "Nigeria",
  },
  {
    name: "Niue",
    code: "NU",
    value: "Niue",
  },
  {
    name: "Norfolk Island",
    code: "NF",
    value: "Norfolk Island",
  },
  {
    name: "North Macedonia",
    code: "MK",
    value: "North Macedonia",
  },
  {
    name: "Northern Mariana Islands",
    code: "MP",
    value: "Northern Mariana Islands",
  },
  {
    name: "Norway",
    code: "NO",
    value: "Norway",
  },
  {
    name: "Oman",
    code: "OM",
    value: "Oman",
  },
  {
    name: "Pakistan",
    code: "PK",
    value: "Pakistan",
  },
  {
    name: "Palau",
    code: "PW",
    value: "Palau",
  },
  {
    name: "Palestine, State of",
    code: "PS",
    value: "Palestine, State of",
  },
  {
    name: "Panama",
    code: "PA",
    value: "Panama",
  },
  {
    name: "Papua New Guinea",
    code: "PG",
    value: "Papua New Guinea",
  },
  {
    name: "Paraguay",
    code: "PY",
    value: "Paraguay",
  },
  {
    name: "Peru",
    code: "PE",
    value: "Peru",
  },
  {
    name: "Philippines",
    code: "PH",
    value: "Philippines",
  },
  {
    name: "Pitcairn",
    code: "PN",
    value: "Pitcairn",
  },
  {
    name: "Poland",
    code: "PL",
    value: "Poland",
  },
  {
    name: "Portugal",
    code: "PT",
    value: "Portugal",
  },
  {
    name: "Puerto Rico",
    code: "PR",
    value: "Puerto Rico",
  },
  {
    name: "Qatar",
    code: "QA",
    value: "Qatar",
  },
  {
    name: "Romania",
    code: "RO",
    value: "Romania",
  },
  {
    name: "Russian Federation",
    code: "RU",
    value: "Russian Federation",
  },
  {
    name: "Rwanda",
    code: "RW",
    value: "Rwanda",
  },
  {
    name: "Réunion",
    code: "RE",
    value: "Réunion",
  },
  {
    name: "Saint Barthélemy",
    code: "BL",
    value: "Saint Barthélemy",
  },
  {
    name: "Saint Helena, Ascension and Tristan da Cunha",
    code: "SH",
    value: "Saint Helena, Ascension and Tristan da Cunha",
  },
  {
    name: "Saint Kitts and Nevis",
    code: "KN",
    value: "Saint Kitts and Nevis",
  },
  {
    name: "Saint Lucia",
    code: "LC",
    value: "Saint Lucia",
  },
  {
    name: "Saint Martin (French part)",
    code: "MF",
    value: "Saint Martin (French part)",
  },
  {
    name: "Saint Pierre and Miquelon",
    code: "PM",
    value: "Saint Pierre and Miquelon",
  },
  {
    name: "Saint Vincent and the Grenadines",
    code: "VC",
    value: "Saint Vincent and the Grenadines",
  },
  {
    name: "Samoa",
    code: "WS",
    value: "Samoa",
  },
  {
    name: "San Marino",
    code: "SM",
    value: "San Marino",
  },
  {
    name: "Sao Tome and Principe",
    code: "ST",
    value: "Sao Tome and Principe",
  },
  {
    name: "Saudi Arabia",
    code: "SA",
    value: "Saudi Arabia",
  },
  { name: "Senegal", value: "Senegal", code: "SN" },
  {
    name: "Serbia",
    code: "RS",
    value: "Serbia",
  },
  {
    name: "Seychelles",
    code: "SC",
    value: "Seychelles",
  },
  { name: "Sierra Leone", value: "Sierra Leone", code: "SL" },
  { name: "Singapore", value: "Singapore", code: "SG" },
  {
    name: "Sint Maarten (Dutch part)",
    value: "Sint Maarten (Dutch part)",
    code: "SX",
  },
  { name: "Slovakia", value: "Slovakia", code: "SK" },
  { name: "Slovenia", value: "Slovenia", code: "SI" },
  { name: "Solomon Islands", value: "Solomon Islands", code: "SB" },
  { name: "Somalia", value: "Somalia", code: "SO" },
  { name: "South Africa", value: "South Africa", code: "ZA" },
  {
    name: "South Georgia and the South Sandwich Islands",
    value: "South Georgia and the South Sandwich Islands",
    code: "GS",
  },
  { name: "South Sudan", value: "South Sudan", code: "SS" },
  { name: "Spain", value: "Spain", code: "ES" },
  { name: "Sri Lanka", value: "Sri Lanka", code: "LK" },
  { name: "Sudan", value: "Sudan", code: "SD" },
  { name: "Suriname", value: "Suriname", code: "SR" },
  {
    name: "Svalbard and Jan Mayen",
    value: "Svalbard and Jan Mayen",
    code: "SJ",
  },
  { name: "Sweden", value: "Sweden", code: "SE" },
  { name: "Switzerland", value: "Switzerland", code: "CH" },
  { name: "Syrian Arab Republic", value: "Syrian Arab Republic", code: "SY" },
  {
    name: "Taiwan (Province of China)",
    value: "Taiwan (Province of China)",
    code: "TW",
  },
  { name: "Tajikistan", value: "Tajikistan", code: "TJ" },
  {
    name: "Tanzania, United Republic of",
    value: "Tanzania, United Republic of",
    code: "TZ",
  },
  { name: "Thailand", value: "Thailand", code: "TH" },
  { name: "Timor-Leste", value: "Timor-Leste", code: "TL" },
  { name: "Togo", value: "Togo", code: "TG" },
  { name: "Tokelau", value: "Tokelau", code: "TK" },
  { name: "Tonga", value: "Tonga", code: "TO" },
  { name: "Trinidad and Tobago", value: "Trinidad and Tobago", code: "TT" },
  { name: "Tunisia", value: "Tunisia", code: "TN" },
  { name: "Turkey", value: "Turkey", code: "TR" },
  { name: "Turkmenistan", value: "Turkmenistan", code: "TM" },
  {
    name: "Turks and Caicos Islands",
    value: "Turks and Caicos Islands",
    code: "TC",
  },
  { name: "Tuvalu", value: "Tuvalu", code: "TV" },
  { name: "Uganda", value: "Uganda", code: "UG" },
  { name: "Ukraine", value: "Ukraine", code: "UA" },
  { name: "United Arab Emirates", value: "United Arab Emirates", code: "AE" },
  { name: "United Kingdom", value: "United Kingdom", code: "GB" },
  {
    name: "United States Minor Outlying Islands",
    value: "United States Minor Outlying Islands",
    code: "UM",
  },
  {
    name: "United States of America",
    value: "United States of America",
    code: "US",
  },
  { name: "Uruguay", value: "Uruguay", code: "UY" },
  { name: "Uzbekistan", value: "Uzbekistan", code: "UZ" },
  { name: "Vanuatu", value: "Vanuatu", code: "VU" },
  {
    name: "Venezuela (Bolivarian Republic of)",
    value: "Venezuela (Bolivarian Republic of)",
    code: "VE",
  },
  { name: "Viet Nam", value: "Viet Nam", code: "VN" },
  {
    name: "Virgin Islands (British)",
    value: "Virgin Islands (British)",
    code: "VG",
  },
  { name: "Virgin Islands (U.S.)", value: "Virgin Islands (U.S.)", code: "VI" },
  { name: "Wallis and Futuna", value: "Wallis and Futuna", code: "WF" },
  { name: "Western Sahara", value: "Western Sahara", code: "EH" },
  { name: "Yemen", value: "Yemen", code: "YE" },
  { name: "Zambia", value: "Zambia", code: "ZM" },
  { name: "Zimbabwe", value: "Zimbabwe", code: "ZW" },
];

// export const languages = [
//   {
//       name: "Abkhaz",
//       value: "Abkhaz",
//       code: "AB"
//   },
//   {
//       name: "Afar",
//       value: "AA"
//   },
//   {
//       name: "Afrikaans",
//       value: "AF"
//   },
//   {
//       name: "Akan",
//       value: "AK"
//   },
//   {
//       name: "Albanian",
//       value: "SQ"
//   },
//   {
//       name: "Amharic",
//       value: "AM"
//   },
//   {
//       name: "Arabic",
//       value: "AR"
//   },
//   {
//       name: "Aragonese",
//       value: "AN"
//   },
//   {
//       name: "Armenian",
//       value: "HY"
//   },
//   {
//       name: "Assamese",
//       value: "AS"
//   },
//   {
//       name: "Avaric",
//       value: "AV"
//   },
//   {
//       name: "Avestan",
//       value: "AE"
//   },
//   {
//       name: "Aymara",
//       value: "AY"
//   },
//   {
//       name: "Azerbaijani",
//       value: "AZ"
//   },
//   {
//       name: "Bambara",
//       value: "BM"
//   },
//   {
//       name: "Bashkir",
//       value: "BA"
//   },
//   {
//       name: "Basque",
//       value: "EU"
//   },
//   {
//       name: "Belarusian",
//       value: "BE"
//   },
//   {
//       name: "Bengali",
//       value: "BN"
//   },
//   {
//       name: "Bihari",
//       value: "BH"
//   },
//   {
//       name: "Bislama",
//       value: "BI"
//   },
//   {
//       name: "Bosnian",
//       value: "BS"
//   },
//   {
//       name: "Breton",
//       value: "BR"
//   },
//   {
//       name: "Bulgarian",
//       value: "BG"
//   },
//   {
//       name: "Burmese",
//       value: "MY"
//   },
//   {
//       name: "Catalan; Valencian",
//       value: "CA"
//   },
//   {
//       name: "Chamorro",
//       value: "CH"
//   },
//   {
//       name: "Chechen",
//       value: "CE"
//   },
//   {
//       name: "Chichewa; Chewa; Nyanja",
//       value: "NY"
//   },
//   {
//       name: "Chinese",
//       value: "ZH"
//   },
//   {
//       name: "Chuvash",
//       value: "CV"
//   },
//   {
//       name: "Cornish",
//       value: "KW"
//   },
//   {
//       name: "Corsican",
//       value: "CO"
//   },
//   {
//       name: "Cree",
//       value: "CR"
//   },
//   {
//       name: "Croatian",
//       value: "HR"
//   },
//   {
//       name: "Czech",
//       value: "CS"
//   },
//   {
//       name: "Danish",
//       value: "DA"
//   },
//   {
//       name: "Divehi; Dhivehi; Maldivian;",
//       value: "DV"
//   },
//   {
//       name: "Dutch",
//       value: "NL"
//   },
//   {
//       name: "English",
//       value: "EN"
//   },
//   {
//       name: "Esperanto",
//       value: "EO"
//   },
//   {
//       name: "Estonian",
//       value: "ET"
//   },
//   {
//       name: "Ewe",
//       value: "EE"
//   },
//   {
//       name: "Faroese",
//       value: "FO"
//   },
//   {
//       name: "Fijian",
//       value: "FJ"
//   },
//   {
//       name: "Finnish",
//       value: "FI"
//   },
//   {
//       name: "French",
//       value: "FR"
//   },
//   {
//       name: "Fula; Fulah; Pulaar; Pular",
//       value: "FF"
//   },
//   {
//       name: "Galician",
//       value: "GL"
//   },
//   {
//       name: "Georgian",
//       value: "KA"
//   },
//   {
//       name: "German",
//       value: "DE"
//   },
//   {
//       name: "Greek, Modern",
//       value: "EL"
//   },
//   {
//       name: "Guaraní",
//       value: "GN"
//   },
//   {
//       name: "Gujarati",
//       value: "GU"
//   },
//   {
//       name: "Haitian; Haitian Creole",
//       value: "HT"
//   },
//   {
//       name: "Hausa",
//       value: "HA"
//   },
//   {
//       name: "Hebrew (modern)",
//       value: "HE"
//   },
//   {
//       name: "Herero",
//       value: "HZ"
//   },
//   {
//       name: "Hindi",
//       value: "HI"
//   },
//   {
//       name: "Hiri Motu",
//       value: "HO"
//   },
//   {
//       name: "Hungarian",
//       value: "HU"
//   },
//   {
//       name: "Interlingua",
//       value: "IA"
//   },
//   {
//       name: "Indonesian",
//       value: "ID"
//   },
//   {
//       name: "Interlingue",
//       value: "IE"
//   },
//   {
//       name: "Irish",
//       value: "GA"
//   },
//   {
//       name: "Igbo",
//       value: "IG"
//   },
//   {
//       name: "Inupiaq",
//       value: "IK"
//   },
//   {
//       name: "Ido",
//       value: "IO"
//   },
//   {
//       name: "Icelandic",
//       value: "IS"
//   },
//   {
//       name: "Italian",
//       value: "IT"
//   },
//   {
//       name: "Inuktitut",
//       value: "IU"
//   },
//   {
//       name: "Japanese",
//       value: "JA"
//   },
//   {
//       name: "Javanese",
//       value: "JV"
//   },
//   {
//       name: "Kalaallisut, Greenlandic",
//       value: "KL"
//   },
//   {
//       name: "Kannada",
//       value: "KN"
//   },
//   {
//       name: "Kanuri",
//       value: "KR"
//   },
//   {
//       name: "Kashmiri",
//       value: "KS"
//   },
//   {
//       name: "Kazakh",
//       value: "KK"
//   },
//   {
//       name: "Khmer",
//       value: "KM"
//   },
//   {
//       name: "Kikuyu, Gikuyu",
//       value: "KI"
//   },
//   {
//       name: "Kinyarwanda",
//       value: "RW"
//   },
//   {
//       name: "Kirghiz, Kyrgyz",
//       value: "KY"
//   },
//   {
//       name: "Komi",
//       value: "KV"
//   },
//   {
//       name: "Kongo",
//       value: "KG"
//   },
//   {
//       name: "Korean",
//       value: "KO"
//   },
//   {
//       name: "Kurdish",
//       value: "KU"
//   },
//   {
//       name: "Kwanyama, Kuanyama",
//       value: "KJ"
//   },
//   {
//       name: "Latin",
//       value: "LA"
//   },
//   {
//       name: "Luxembourgish, Letzeburgesch",
//       value: "LB"
//   },
//   {
//       name: "Luganda",
//       value: "LG"
//   },
//   {
//       name: "Limburgish, Limburgan, Limburger",
//       value: "LI"
//   },
//   {
//       name: "Lingala",
//       value: "LN"
//   },
//   {
//       name: "Lao",
//       value: "LO"
//   },
//   {
//       name: "Lithuanian",
//       value: "LT"
//   },
//   {
//       name: "Luba-Katanga",
//       value: "LU"
//   },
//   {
//       name: "Latvian",
//       value: "LV"
//   },
//   {
//       name: "Manx",
//       value: "GV"
//   },
//   {
//       name: "Macedonian",
//       value: "MK"
//   },
//   {
//       name: "Malagasy",
//       value: "MG"
//   },
//   {
//       name: "Malay",
//       value: "MS"
//   },
//   {
//       name: "Malayalam",
//       value: "ML"
//   },
//   {
//       name: "Maltese",
//       value: "MT"
//   },
//   {
//       name: "Māori",
//       value: "MI"
//   },
//   {
//       name: "Marathi (Marāṭhī)",
//       value: "MR"
//   },
//   {
//       name: "Marshallese",
//       value: "MH"
//   },
//   {
//       name: "Mongolian",
//       value: "MN"
//   },
//   {
//       name: "Nauru",
//       value: "NA"
//   },
//   {
//       name: "Navajo, Navaho",
//       value: "NV"
//   },
//   {
//       name: "Norwegian Bokmål",
//       value: "NB"
//   },
//   {
//       name: "North Ndebele",
//       value: "ND"
//   },
//   {
//       name: "Nepali",
//       value: "NE"
//   },
//   {
//       name: "Ndonga",
//       value: "NG"
//   },
//   {
//       name: "Norwegian Nynorsk",
//       value: "NN"
//   },
//   {
//       name: "Norwegian",
//       value: "NO"
//   },
//   {
//       name: "Nuosu",
//       value: "II"
//   },
//   {
//       name: "South Ndebele",
//       value: "NR"
//   },
//   {
//       name: "Occitan",
//       value: "OC"
//   },
//   {
//       name: "Ojibwe, Ojibwa",
//       value: "OJ"
//   },
//   {
//       name: "Old Church Slavonic, Church Slavonic, Old Bulgarian",
//       value: "CU"
//   },
//   {
//       name: "Oromo",
//       value: "OM"
//   },
//   {
//       name: "Oriya",
//       value: "OR"
//   },
//   {
//       name: "Ossetian, Ossetic",
//       value: "OS"
//   },
//   {
//       name: "Panjabi, Punjabi",
//       value: "PA"
//   },
//   {
//       name: "Pāli",
//       value: "PI"
//   },
//   {
//       name: "Persian",
//       value: "FA"
//   },
//   {
//       name: "Polish",
//       value: "PL"
//   },
//   {
//       name: "Pashto, Pushto",
//       value: "PS"
//   },
//   {
//       name: "Portuguese",
//       value: "PT"
//   },
//   {
//       name: "Quechua",
//       value: "QU"
//   },
//   {
//       name: "Romansh",
//       value: "RM"
//   },
//   {
//       name: "Kirundi",
//       value: "RN"
//   },
//   {
//       name: "Russian",
//       value: "RU"
//   },
//   {
//       name: "Sanskrit (Saṁskṛta)",
//       value: "SA"
//   },
//   {
//       name: "Sardinian",
//       value: "SC"
//   },
//   {
//       name: "Sindhi",
//       value: "SD"
//   },
//   {
//       name: "Northern Sami",
//       value: "SE"
//   },
//   {
//       name: "Samoan",
//       value: "SM"
//   },
//   {
//       name: "Sango",
//       value: "SG"
//   },
//   {
//       name: "Serbian",
//       value: "SR"
//   },
//   {
//       name: "Scottish Gaelic; Gaelic",
//       value: "GD"
//   },
//   {
//       name: "Shona",
//       value: "SN"
//   },
//   {
//       name: "Sinhala, Sinhalese",
//       value: "SI"
//   },
//   {
//       name: "Slovak",
//       value: "SK"
//   },
//   {
//       name: "Slovene",
//       value: "SL"
//   },
//   {
//       name: "Somali",
//       value: "SO"
//   },
//   {
//       name: "Southern Sotho",
//       value: "ST"
//   },
//   {
//       name: "Spanish; Castilian",
//       value: "ES"
//   },
//   {
//       name: "Sundanese",
//       value: "SU"
//   },
//   {
//       name: "Swahili",
//       value: "SW"
//   },
//   {
//       name: "Swati",
//       value: "SS"
//   },
//   {
//       name: "Swedish",
//       value: "SV"
//   },
//   {
//       name: "Tamil",
//       value: "TA"
//   },
//   {
//       name: "Telugu",
//       value: "TE"
//   },
//   {
//       name: "Tajik",
//       value: "TG"
//   },
//   {
//       name: "Thai",
//       value: "TH"
//   },
//   {
//       name: "Tigrinya",
//       value: "TI"
//   },
//   {
//       name: "Tibetan Standard, Tibetan, Central",
//       value: "BO"
//   },
//   {
//       name: "Turkmen",
//       value: "TK"
//   },
//   {
//       name: "Tagalog",
//       value: "TL"
//   },
//   {
//       name: "Tswana",
//       value: "TN"
//   },
//   {
//       name: "Tonga (Tonga Islands)",
//       value: "TO"
//   },
//   {
//       name: "Turkish",
//       value: "TR"
//   },
//   {
//       name: "Tsonga",
//       value: "TS"
//   },
//   {
//       name: "Tatar",
//       value: "TT"
//   },
//   {
//       name: "Twi",
//       value: "TW"
//   },
//   {
//       name: "Tahitian",
//       value: "TY"
//   },
//   {
//       name: "Uighur, Uyghur",
//       value: "UG"
//   },
//   {
//       name: "Ukrainian",
//       value: "UK"
//   },
//   {
//       name: "Urdu",
//       value: "UR"
//   },
//   {
//       name: "Uzbek",
//       value: "UZ"
//   },
//   {
//       name: "Venda",
//       value: "VE"
//   },
//   {
//       name: "Vietnamese",
//       value: "VI"
//   },
//   {
//       name: "Volapük",
//       value: "VO"
//   },
//   {
//       name: "Walloon",
//       value: "WA"
//   },
//   {
//       name: "Welsh",
//       value: "CY"
//   },
//   {
//       name: "Wolof",
//       value: "WO"
//   },
//   {
//       name: "Western Frisian",
//       value: "FY"
//   },
//   {
//       name: "Xhosa",
//       value: "XH"
//   },
//   {
//       name: "Yiddish",
//       value: "YI"
//   },
//   {
//       name: "Yoruba",
//       value: "YO"
//   },
//   {
//       name: "Zhuang, Chuang",
//       value: "ZA"
//   },
//   {
//       name: "Zulu",
//       value: "ZU"
//   }
// ];

export const languages = [
  {
    name: "Abkhaz",
    value: "Abkhaz",
    code: "AB",
  },
  {
    name: "Afar",
    value: "Afar",
    code: "AA",
  },
  {
    name: "Afrikaans",
    value: "Afrikaans",
    code: "AF",
  },
  {
    name: "Akan",
    value: "Akan",
    code: "AK",
  },
  {
    name: "Albanian",
    value: "Albanian",
    code: "SQ",
  },
  {
    name: "Amharic",
    value: "Amharic",
    code: "AM",
  },
  {
    name: "Arabic",
    value: "Arabic",
    code: "AR",
  },
  {
    name: "Aragonese",
    value: "Aragonese",
    code: "AN",
  },
  {
    name: "Armenian",
    value: "Armenian",
    code: "HY",
  },
  {
    name: "Assamese",
    value: "Assamese",
    code: "AS",
  },
  {
    name: "Avaric",
    value: "Avaric",
    code: "AV",
  },
  {
    name: "Avestan",
    value: "Avestan",
    code: "AE",
  },
  {
    name: "Aymara",
    value: "Aymara",
    code: "AY",
  },
  {
    name: "Azerbaijani",
    value: "Azerbaijani",
    code: "AZ",
  },
  {
    name: "Bambara",
    value: "Bambara",
    code: "BM",
  },
  {
    name: "Bashkir",
    value: "Bashkir",
    code: "BA",
  },
  {
    name: "Basque",
    value: "Basque",
    code: "EU",
  },
  {
    name: "Belarusian",
    value: "Belarusian",
    code: "BE",
  },
  {
    name: "Bengali",
    value: "Bengali",
    code: "BN",
  },
  {
    name: "Bihari",
    value: "Bihari",
    code: "BH",
  },
  {
    name: "Bislama",
    value: "Bislama",
    code: "BI",
  },
  {
    name: "Bosnian",
    value: "Bosnian",
    code: "BS",
  },
  {
    name: "Breton",
    value: "Breton",
    code: "BR",
  },
  {
    name: "Bulgarian",
    value: "Bulgarian",
    code: "BG",
  },
  {
    name: "Burmese",
    value: "Burmese",
    code: "MY",
  },
  {
    name: "Catalan; Valencian",
    value: "Catalan; Valencian",
    code: "CA",
  },
  {
    name: "Chamorro",
    value: "Chamorro",
    code: "CH",
  },
  {
    name: "Chechen",
    value: "Chechen",
    code: "CE",
  },
  {
    name: "Chichewa; Chewa; Nyanja",
    value: "Chichewa; Chewa; Nyanja",
    code: "NY",
  },
  {
    name: "Chinese",
    value: "Chinese",
    code: "ZH",
  },
  {
    name: "Chuvash",
    value: "Chuvash",
    code: "CV",
  },
  {
    name: "Cornish",
    value: "Cornish",
    code: "KW",
  },
  {
    name: "Corsican",
    value: "Corsican",
    code: "CO",
  },
  {
    name: "Cree",
    value: "Cree",
    code: "CR",
  },
  {
    name: "Croatian",
    value: "Croatian",
    code: "HR",
  },
  {
    name: "Czech",
    value: "Czech",
    code: "CS",
  },
  {
    name: "Danish",
    value: "Danish",
    code: "DA",
  },
  {
    name: "Divehi; Dhivehi; Maldivian;",
    value: "Divehi; Dhivehi; Maldivian;",
    code: "DV",
  },
  {
    name: "Dutch",
    value: "Dutch",
    code: "NL",
  },
  {
    name: "English",
    value: "English",
    code: "EN",
  },
  {
    name: "Esperanto",
    value: "Esperanto",
    code: "EO",
  },
  {
    name: "Estonian",
    value: "Estonian",
    code: "ET",
  },
  {
    name: "Ewe",
    value: "Ewe",
    code: "EE",
  },
  {
    name: "Faroese",
    value: "Faroese",
    code: "FO",
  },
  {
    name: "Fijian",
    value: "Fijian",
    code: "FJ",
  },
  {
    name: "Finnish",
    value: "Finnish",
    code: "FI",
  },
  {
    name: "French",
    value: "French",
    code: "FR",
  },
  {
    name: "Fula; Fulah; Pulaar; Pular",
    value: "Fula; Fulah; Pulaar; Pular",
    code: "FF",
  },
  {
    name: "Galician",
    value: "Galician",
    code: "GL",
  },
  {
    name: "Georgian",
    value: "Georgian",
    code: "KA",
  },
  {
    name: "German",
    value: "German",
    code: "DE",
  },
  {
    name: "Greek, Modern",
    value: "Greek, Modern",
    code: "EL",
  },
  {
    name: "Guaraní",
    value: "Guaraní",
    code: "GN",
  },
  {
    name: "Gujarati",
    value: "Gujarati",
    code: "GU",
  },
  {
    name: "Haitian; Haitian Creole",
    value: "Haitian; Haitian Creole",
    code: "HT",
  },
  {
    name: "Hausa",
    value: "Hausa",
    code: "HA",
  },
  {
    name: "Hebrew (modern)",
    value: "Hebrew (modern)",
    code: "HE",
  },
  {
    name: "Herero",
    value: "Herero",
    code: "HZ",
  },
  {
    name: "Hindi",
    value: "Hindi",
    code: "HI",
  },
  {
    name: "Hiri Motu",
    value: "Hiri Motu",
    code: "HO",
  },
  {
    name: "Hungarian",
    value: "Hungarian",
    code: "HU",
  },
  {
    name: "Interlingua",
    value: "Interlingua",
    code: "IA",
  },
  {
    name: "Indonesian",
    value: "Indonesian",
    code: "ID",
  },
  {
    name: "Interlingue",
    value: "Interlingue",
    code: "IE",
  },
  {
    name: "Irish",
    value: "Irish",
    code: "GA",
  },
  {
    name: "Igbo",
    value: "Igbo",
    code: "IG",
  },
  {
    name: "Inupiaq",
    value: "Inupiaq",
    code: "IK",
  },
  {
    name: "Ido",
    value: "Ido",
    code: "IO",
  },
  {
    name: "Icelandic",
    value: "Icelandic",
    code: "IS",
  },
  {
    name: "Italian",
    value: "Italian",
    code: "IT",
  },
  {
    name: "Inuktitut",
    value: "Inuktitut",
    code: "IU",
  },
  {
    name: "Japanese",
    value: "Japanese",
    code: "JA",
  },
  {
    name: "Javanese",
    value: "Javanese",
    code: "JV",
  },
  {
    name: "Kalaallisut, Greenlandic",
    value: "Kalaallisut, Greenlandic",
    code: "KL",
  },
  {
    name: "Kannada",
    value: "Kannada",
    code: "KN",
  },
  {
    name: "Kanuri",
    value: "Kanuri",
    code: "KR",
  },
  {
    name: "Kashmiri",
    value: "Kashmiri",
    code: "KS",
  },
  {
    name: "Kazakh",
    value: "Kazakh",
    code: "KK",
  },
  {
    name: "Khmer",
    value: "Khmer",
    code: "KM",
  },
  {
    name: "Kikuyu, Gikuyu",
    value: "Kikuyu, Gikuyu",
    code: "KI",
  },
  {
    name: "Kinyarwanda",
    value: "Kinyarwanda",
    code: "RW",
  },
  {
    name: "Kirghiz, Kyrgyz",
    value: "Kirghiz, Kyrgyz",
    code: "KY",
  },
  {
    name: "Komi",
    value: "Komi",
    code: "KV",
  },
  {
    name: "Kongo",
    value: "Kongo",
    code: "KG",
  },
  {
    name: "Korean",
    value: "Korean",
    code: "KO",
  },
  {
    name: "Kurdish",
    value: "Kurdish",
    code: "KU",
  },
  {
    name: "Kwanyama, Kuanyama",
    value: "Kwanyama, Kuanyama",
    code: "KJ",
  },
  {
    name: "Latin",
    value: "Latin",
    code: "LA",
  },
  {
    name: "Luxembourgish, Letzeburgesch",
    value: "Luxembourgish, Letzeburgesch",
    code: "LB",
  },
  {
    name: "Luganda",
    value: "Luganda",
    code: "LG",
  },
  {
    name: "Limburgish, Limburgan, Limburger",
    value: "Limburgish, Limburgan, Limburger",
    code: "LI",
  },
  {
    name: "Lingala",
    value: "Lingala",
    code: "LN",
  },
  {
    name: "Lao",
    value: "Lao",
    code: "LO",
  },
  {
    name: "Lithuanian",
    value: "Lithuanian",
    code: "LT",
  },
  {
    name: "Luba-Katanga",
    value: "Luba-Katanga",
    code: "LU",
  },
  {
    name: "Latvian",
    value: "Latvian",
    code: "LV",
  },
  {
    name: "Manx",
    value: "Manx",
    code: "GV",
  },
  {
    name: "Macedonian",
    value: "Macedonian",
    code: "MK",
  },
  {
    name: "Malagasy",
    value: "Malagasy",
    code: "MG",
  },
  {
    name: "Malay",
    value: "Malay",
    code: "MS",
  },
  {
    name: "Malayalam",
    value: "Malayalam",
    code: "ML",
  },
  {
    name: "Maltese",
    value: "Maltese",
    code: "MT",
  },
  {
    name: "Māori",
    value: "Māori",
    code: "MI",
  },
  {
    name: "Marathi (Marāṭhī)",
    value: "Marathi (Marāṭhī)",
    code: "MR",
  },
  {
    name: "Marshallese",
    value: "Marshallese",
    code: "MH",
  },
  {
    name: "Mongolian",
    value: "Mongolian",
    code: "MN",
  },
  {
    name: "Nauru",
    value: "Nauru",
    code: "NA",
  },
  {
    name: "Navajo, Navaho",
    value: "Navajo, Navaho",
    code: "NV",
  },
  {
    name: "Norwegian Bokmål",
    value: "Norwegian Bokmål",
    code: "NB",
  },
  {
    name: "North Ndebele",
    value: "North Ndebele",
    code: "ND",
  },
  {
    name: "Nepali",
    value: "Nepali",
    code: "NE",
  },
  {
    name: "Ndonga",
    value: "Ndonga",
    code: "NG",
  },
  {
    name: "Norwegian Nynorsk",
    value: "Norwegian Nynorsk",
    code: "NN",
  },
  {
    name: "Norwegian",
    value: "Norwegian",
    code: "NO",
  },
  {
    name: "Nuosu",
    value: "Nuosu",
    code: "II",
  },
  {
    name: "South Ndebele",
    value: "South Ndebele",
    code: "NR",
  },
  {
    name: "Occitan",
    value: "Occitan",
    code: "OC",
  },
  {
    name: "Ojibwe, Ojibwa",
    value: "Ojibwe, Ojibwa",
    code: "OJ",
  },
  {
    name: "Old Church Slavonic, Church Slavonic, Old Bulgarian",
    value: "Old Church Slavonic, Church Slavonic, Old Bulgarian",
    code: "CU",
  },
  {
    name: "Oromo",
    value: "Oromo",
    code: "OM",
  },
  {
    name: "Oriya",
    value: "Oriya",
    code: "OR",
  },
  {
    name: "Ossetian, Ossetic",
    value: "Ossetian, Ossetic",
    code: "OS",
  },
  {
    name: "Panjabi, Punjabi",
    value: "Panjabi, Punjabi",
    code: "PA",
  },
  {
    name: "Pāli",
    value: "Pāli",
    code: "PI",
  },
  {
    name: "Persian",
    value: "Persian",
    code: "FA",
  },
  {
    name: "Polish",
    value: "Polish",
    code: "PL",
  },
  {
    name: "Pashto, Pushto",
    value: "Pashto, Pushto",
    code: "PS",
  },
  {
    name: "Portuguese",
    value: "Portuguese",
    code: "PT",
  },
  {
    name: "Quechua",
    value: "Quechua",
    code: "QU",
  },
  {
    name: "Romansh",
    value: "Romansh",
    code: "RM",
  },
  {
    name: "Kirundi",
    value: "Kirundi",
    code: "RN",
  },
  {
    name: "Russian",
    value: "Russian",
    code: "RU",
  },
  {
    name: "Sanskrit (Saṁskṛta)",
    value: "Sanskrit (Saṁskṛta)",
    code: "SA",
  },
  {
    name: "Sardinian",
    value: "Sardinian",
    code: "SC",
  },
  {
    name: "Sindhi",
    value: "Sindhi",
    code: "SD",
  },
  {
    name: "Northern Sami",
    value: "Northern Sami",
    code: "SE",
  },
  {
    name: "Samoan",
    value: "Samoan",
    code: "SM",
  },
  {
    name: "Sango",
    value: "Sango",
    code: "SG",
  },
  {
    name: "Serbian",
    value: "Serbian",
    code: "SR",
  },
  {
    name: "Scottish Gaelic; Gaelic",
    value: "Scottish Gaelic; Gaelic",
    code: "GD",
  },
  {
    name: "Shona",
    value: "Shona",
    code: "SN",
  },
  {
    name: "Sinhala, Sinhalese",
    value: "Sinhala, Sinhalese",
    code: "SI",
  },
  {
    name: "Slovak",
    value: "Slovak",
    code: "SK",
  },
  {
    name: "Slovene",
    value: "Slovene",
    code: "SL",
  },
  {
    name: "Somali",
    value: "Somali",
    code: "SO",
  },
  {
    name: "Southern Sotho",
    value: "Southern Sotho",
    code: "ST",
  },
  {
    name: "Spanish; Castilian",
    value: "Spanish; Castilian",
    code: "ES",
  },
  {
    name: "Sundanese",
    value: "Sundanese",
    code: "SU",
  },
  {
    name: "Swahili",
    value: "Swahili",
    code: "SW",
  },
  {
    name: "Swati",
    value: "Swati",
    code: "SS",
  },
  {
    name: "Swedish",
    value: "Swedish",
    code: "SV",
  },
  {
    name: "Tamil",
    value: "Tamil",
    code: "TA",
  },
  {
    name: "Telugu",
    value: "Telugu",
    code: "TE",
  },
  {
    name: "Tajik",
    value: "Tajik",
    code: "TG",
  },
  {
    name: "Thai",
    value: "Thai",
    code: "TH",
  },
  {
    name: "Tigrinya",
    value: "Tigrinya",
    code: "TI",
  },
  {
    name: "Tibetan Standard, Tibetan, Central",
    value: "Tibetan Standard, Tibetan, Central",
    code: "BO",
  },
  {
    name: "Turkmen",
    value: "Turkmen",
    code: "TK",
  },
  {
    name: "Tagalog",
    value: "Tagalog",
    code: "TL",
  },
  {
    name: "Tswana",
    value: "Tswana",
    code: "TN",
  },
  {
    name: "Tonga (Tonga Islands)",
    value: "Tonga (Tonga Islands)",
    code: "TO",
  },
  {
    name: "Turkish",
    value: "Turkish",
    code: "TR",
  },
  {
    name: "Tsonga",
    value: "Tsonga",
    code: "TS",
  },
  {
    name: "Tatar",
    value: "Tatar",
    code: "TT",
  },
  {
    name: "Twi",
    value: "Twi",
    code: "TW",
  },
  {
    name: "Tahitian",
    value: "Tahitian",
    code: "TY",
  },
  {
    name: "Uighur, Uyghur",
    value: "Uighur, Uyghur",
    code: "UG",
  },
  {
    name: "Ukrainian",
    value: "Ukrainian",
    code: "UK",
  },
  {
    name: "Urdu",
    value: "Urdu",
    code: "UR",
  },
  {
    name: "Uzbek",
    value: "Uzbek",
    code: "UZ",
  },
  {
    name: "Venda",
    value: "Venda",
    code: "VE",
  },
  {
    name: "Vietnamese",
    value: "Vietnamese",
    code: "VI",
  },
  {
    name: "Volapük",
    value: "Volapük",
    code: "VO",
  },
  {
    name: "Walloon",
    value: "Walloon",
    code: "WA",
  },
  {
    name: "Welsh",
    value: "Welsh",
    code: "CY",
  },
  {
    name: "Wolof",
    value: "Wolof",
    code: "WO",
  },
  {
    name: "Western Frisian",
    value: "Western Frisian",
    code: "FY",
  },
  {
    name: "Xhosa",
    value: "Xhosa",
    code: "XH",
  },
  {
    name: "Yiddish",
    value: "Yiddish",
    code: "YI",
  },
  {
    name: "Yoruba",
    value: "Yoruba",
    code: "YO",
  },
  {
    name: "Zhuang, Chuang",
    value: "Zhuang, Chuang",
    code: "ZA",
  },
  {
    name: "Zulu",
    value: "Zulu",
    code: "ZU",
  },
];

export const timezones = [
  { name: "Pacific/Midway (GMT-11:00)", value: "GMT-11:00" },
  { name: "Pacific/Niue (GMT-11:00)", value: "GMT-11:00" },
  { name: "Pacific/Pago_Pago (GMT-11:00)", value: "GMT-11:00" },
  { name: "America/Adak (GMT-10:00)", value: "GMT-10:00" },
  { name: "Pacific/Honolulu (GMT-10:00)", value: "GMT-10:00" },
  { name: "Pacific/Rarotonga (GMT-10:00)", value: "GMT-10:00" },
  { name: "Pacific/Tahiti (GMT-10:00)", value: "GMT-10:00" },
  { name: "Pacific/Marquesas (GMT-09:30)", value: "GMT-09:30" },
  { name: "America/Anchorage (GMT-09:00)", value: "GMT-09:00" },
  { name: "America/Juneau (GMT-09:00)", value: "GMT-09:00" },
  { name: "America/Metlakatla (GMT-09:00)", value: "GMT-09:00" },
  { name: "America/Nome (GMT-09:00)", value: "GMT-09:00" },
  { name: "America/Sitka (GMT-09:00)", value: "GMT-09:00" },
  { name: "America/Yakutat (GMT-09:00)", value: "GMT-09:00" },
  { name: "Pacific/Gambier (GMT-09:00)", value: "GMT-09:00" },
  { name: "America/Los_Angeles (GMT-08:00)", value: "GMT-08:00" },
  { name: "America/Tijuana (GMT-08:00)", value: "GMT-08:00" },
  { name: "America/Vancouver (GMT-08:00)", value: "GMT-08:00" },
  { name: "Pacific/Pitcairn (GMT-08:00)", value: "GMT-08:00" },
  { name: "America/Boise (GMT-07:00)", value: "GMT-07:00" },
  { name: "America/Cambridge_Bay (GMT-07:00)", value: "GMT-07:00" },
  { name: "America/Chihuahua (GMT-07:00)", value: "GMT-07:00" },
  { name: "America/Creston (GMT-07:00)", value: "GMT-07:00" },
  { name: "America/Dawson (GMT-07:00)", value: "GMT-07:00" },
  { name: "America/Dawson_Creek (GMT-07:00)", value: "GMT-07:00" },
  { name: "America/Denver (GMT-07:00)", value: "GMT-07:00" },
  { name: "America/Edmonton (GMT-07:00)", value: "GMT-07:00" },
  { name: "America/Fort_Nelson (GMT-07:00)", value: "GMT-07:00" },
  { name: "America/Hermosillo (GMT-07:00)", value: "GMT-07:00" },
  { name: "America/Inuvik (GMT-07:00)", value: "GMT-07:00" },
  { name: "America/Ojinaga (GMT-07:00)", value: "GMT-07:00" },
  { name: "America/Phoenix (GMT-07:00)", value: "GMT-07:00" },
  { name: "America/Whitehorse (GMT-07:00)", value: "GMT-07:00" },
  { name: "America/Yellowknife (GMT-07:00)", value: "GMT-07:00" },
  { name: "America/Bahia_Banderas (GMT-07:00)", value: "GMT-07:00" },
  { name: "America/Belize (GMT-06:00)", value: "GMT-06:00" },
  { name: "America/Chicago (GMT-06:00)", value: "GMT-06:00" },
  { name: "America/Costa_Rica (GMT-06:00)", value: "GMT-06:00" },
  { name: "America/El_Salvador (GMT-06:00)", value: "GMT-06:00" },
  { name: "America/Guatemala (GMT-06:00)", value: "GMT-06:00" },
  { name: "America/Indiana/Knox (GMT-06:00)", value: "GMT-06:00" },
  { name: "America/Indiana/Tell_City (GMT-06:00)", value: "GMT-06:00" },
  { name: "America/Managua (GMT-06:00)", value: "GMT-06:00" },
  { name: "America/Matamoros (GMT-06:00)", value: "GMT-06:00" },
  { name: "America/Menominee (GMT-06:00)", value: "GMT-06:00" },
  { name: "America/Merida (GMT-06:00)", value: "GMT-06:00" },
  { name: "America/Mexico_City (GMT-06:00)", value: "GMT-06:00" },
  { name: "America/Monterrey (GMT-06:00)", value: "GMT-06:00" },
  { name: "America/North_Dakota/Beulah (GMT-06:00)", value: "GMT-06:00" },
  { name: "America/North_Dakota/Center (GMT-06:00)", value: "GMT-06:00" },
  { name: "America/North_Dakota/Beulah (GMT-06:00)", value: "GMT-06:00" },
  { name: "America/North_Dakota/New_Salem (GMT-06:00)", value: "GMT-06:00" },
  { name: "America/Regina (GMT-06:00)", value: "GMT-06:00" },
  { name: "America/Resolute (GMT-06:00)", value: "GMT-06:00" },
  { name: "America/Swift_Current (GMT-06:00)", value: "GMT-06:00" },
  { name: "America/Tegucigalpa (GMT-06:00)", value: "GMT-06:00" },
  { name: "America/Winnipeg (GMT-06:00)", value: "GMT-06:00" },
  { name: "Pacific/Easter (GMT-06:00)", value: "GMT-06:00" },
  { name: "Pacific/Galapagos (GMT-06:00)", value: "GMT-06:00" },
  { name: "America/Atikokan (GMT-05:00)", value: "GMT-05:00" },
  { name: "America/Bogota (GMT-05:00)", value: "GMT-05:00" },
  { name: "America/Cancun (GMT-05:00)", value: "GMT-05:00" },
  { name: "America/Cayman (GMT-05:00)", value: "GMT-05:00" },
  { name: "America/Detroit (GMT-05:00)", value: "GMT-05:00" },
  { name: "America/Eirunepe (GMT-05:00)", value: "GMT-05:00" },
  { name: "America/Grand_Turk (GMT-05:00)", value: "GMT-05:00" },
  { name: "America/Guayaquil (GMT-05:00)", value: "GMT-05:00" },
  { name: "America/Havana (GMT-05:00)", value: "GMT-05:00" },
  { name: "America/Indiana/Indianapolis (GMT-05:00)", value: "GMT-05:00" },
  { name: "America/Indiana/Marengo (GMT-05:00)", value: "GMT-05:00" },
  { name: "America/Indiana/Petersburg (GMT-05:00)", value: "GMT-05:00" },
  { name: "America/Indiana/Vevay (GMT-05:00)", value: "GMT-05:00" },
  { name: "America/Indiana/Vincennes (GMT-05:00)", value: "GMT-05:00" },
  { name: "America/Indiana/Winamac (GMT-05:00)", value: "GMT-05:00" },
  { name: "America/Iqaluit (GMT-05:00)", value: "GMT-05:00" },
  { name: "America/Jamaica (GMT-05:00)", value: "GMT-05:00" },
  { name: "America/Kentucky/Louisville (GMT-05:00)", value: "GMT-05:00" },
  { name: "America/Kentucky/Monticello (GMT-05:00)", value: "GMT-05:00" },
  { name: "America/Lima (GMT-05:00)", value: "GMT-05:00" },
  { name: "America/Nassau (GMT-05:00)", value: "GMT-05:00" },
  { name: "America/New_York (GMT-05:00)", value: "GMT-05:00" },
  { name: "America/Nipigon (GMT-05:00)", value: "GMT-05:00" },
  { name: "America/Panama (GMT-05:00)", value: "GMT-05:00" },
  { name: "America/Pangnirtung (GMT-05:00)", value: "GMT-05:00" },
  { name: "America/Port-au-Prince (GMT-05:00)", value: "GMT-05:00" },
  { name: "America/Rio_Branco (GMT-05:00)", value: "GMT-05:00" },
  { name: "America/Thunder_Bay (GMT-05:00)", value: "GMT-05:00" },
  { name: "America/Toronto (GMT-05:00)", value: "GMT-05:00" },
  { name: "America/AnguillaSandy Hill (GMT-04:00)", value: "GMT-04:00" },
  { name: "America/Antigua (GMT-04:00)", value: "GMT-04:00" },
  { name: "America/Aruba (GMT-04:00)", value: "GMT-04:00" },
  { name: "America/Asuncion (GMT-04:00)", value: "GMT-04:00" },
  { name: "America/Barbados (GMT-04:00)", value: "GMT-04:00" },
  { name: "America/Blanc-Sablon (GMT-04:00)", value: "GMT-04:00" },
  { name: "America/Boa_Vista (GMT-04:00)", value: "GMT-04:00" },
  { name: "America/Campo_Grande (GMT-04:00)", value: "GMT-04:00" },
  { name: "America/Caracas (GMT-04:00)", value: "GMT-04:00" },
  { name: "America/Cuiaba (GMT-04:00)", value: "GMT-04:00" },
  { name: "America/Curacao (GMT-04:00)", value: "GMT-04:00" },
  { name: "America/Dominica (GMT-04:00)", value: "GMT-04:00" },
  { name: "America/Glace_Bay (GMT-04:00)", value: "GMT-04:00" },
  { name: "America/Goose_Bay (GMT-04:00)", value: "GMT-04:00" },
  { name: "America/Grenada (GMT-04:00)", value: "GMT-04:00" },
  { name: "America/Guadeloupe (GMT-04:00)", value: "GMT-04:00" },
  { name: "America/Guyana (GMT-04:00)", value: "GMT-04:00" },
  { name: "America/Halifax (GMT-04:00)", value: "GMT-04:00" },
  { name: "America/Kralendijk (GMT-04:00)", value: "GMT-04:00" },
  { name: "America/La_Paz (GMT-04:00)", value: "GMT-04:00" },
  { name: "America/Lower_Princes (GMT-04:00)", value: "GMT-04:00" },
  { name: "America/Manaus (GMT-04:00)", value: "GMT-04:00" },
  { name: "America/Marigot (GMT-04:00)", value: "GMT-04:00" },
  { name: "America/Martinique (GMT-04:00)", value: "GMT-04:00" },
  { name: "America/Moncton (GMT-04:00)", value: "GMT-04:00" },
  { name: "America/Montserrat (GMT-04:00)", value: "GMT-04:00" },
  { name: "America/Porto_Velho (GMT-04:00)", value: "GMT-04:00" },
  { name: "America/Port_of_Spain (GMT-04:00)", value: "GMT-04:00" },
  { name: "America/Puerto_Rico (GMT-04:00)", value: "GMT-04:00" },
  { name: "America/Santiago (GMT-04:00)", value: "GMT-04:00" },
  { name: "America/Santo_Domingo (GMT-04:00)", value: "GMT-04:00" },
  { name: "America/St_Barthelemy (GMT-04:00)", value: "GMT-04:00" },
  { name: "America/St_Kitts (GMT-04:00)", value: "GMT-04:00" },
  { name: "America/St_Lucia (GMT-04:00)", value: "GMT-04:00" },
  { name: "America/St_Thomas (GMT-04:00)", value: "GMT-04:00" },
  { name: "America/St_Vincent (GMT-04:00)", value: "GMT-04:00" },
  { name: "America/Thule (GMT-04:00)", value: "GMT-04:00" },
  { name: "America/Tortola (GMT-04:00)", value: "GMT-04:00" },
  { name: "Atlantic/Bermuda (GMT-04:00)", value: "GMT-04:00" },
  { name: "America/St_Johns (GMT-03:00)", value: "GMT-03:30" },
  { name: "America/Araguaina (GMT-03:00)", value: "GMT-03:00" },
  { name: "America/Argentina/Buenos_Aires (GMT-03:00)", value: "GMT-03:00" },
  { name: "America/Argentina/Catamarca (GMT-03:00)", value: "GMT-03:00" },
  { name: "America/Argentina/Cordoba (GMT-03:00)", value: "GMT-03:00" },
  { name: "America/Argentina/Jujuy (GMT-03:00)", value: "GMT-03:00" },
  { name: "America/Argentina/La_Rioja (GMT-03:00)", value: "GMT-03:00" },
  { name: "America/Argentina/Mendoza (GMT-03:00)", value: "GMT-03:00" },
  { name: "America/Argentina/Rio_Gallegos (GMT-03:00)", value: "GMT-03:00" },
  { name: "America/Argentina/Salta (GMT-03:00)", value: "GMT-03:00" },
  { name: "America/Argentina/San_Juan (GMT-03:00)", value: "GMT-03:00" },
  { name: "America/Argentina/San_Luis (GMT-03:00)", value: "GMT-03:00" },
  { name: "America/Argentina/Tucuman (GMT-03:00)", value: "GMT-03:00" },
  { name: "America/Argentina/Ushuaia (GMT-03:00)", value: "GMT-03:00" },
  { name: "America/Bahia (GMT-03:00)", value: "GMT-03:00" },
  { name: "America/Belem (GMT-03:00)", value: "GMT-03:00" },
  { name: "America/Cayenne (GMT-03:00)", value: "GMT-03:00" },
  { name: "America/Fortaleza (GMT-03:00)", value: "GMT-03:00" },
  { name: "America/Godthab (GMT-03:00)", value: "GMT-03:00" },
  { name: "America/Maceio (GMT-03:00)", value: "GMT-03:00" },
  { name: "America/Miquelon (GMT-03:00)", value: "GMT-03:00" },
  { name: "America/Montevideo (GMT-03:00)", value: "GMT-03:00" },
  { name: "America/Paramaribo (GMT-03:00)", value: "GMT-03:00" },
  { name: "America/Punta_Arenas (GMT-03:00)", value: "GMT-03:00" },
  { name: "America/Recife (GMT-03:00)", value: "GMT-03:00" },
  { name: "America/Santarem (GMT-03:00)", value: "GMT-03:00" },
  { name: "America/Sao_Paulo (GMT-03:00)", value: "GMT-03:00" },
  { name: "Antarctica/Palmer (GMT-03:00)", value: "GMT-03:00" },
  { name: "Antarctica/Rothera (GMT-03:00)", value: "GMT-03:00" },
  { name: "Atlantic/Stanley (GMT-03:00)", value: "GMT-03:00" },
  { name: "America/Noronha (GMT-02:00)", value: "GMT-02:00" },
  { name: "Atlantic/South_Georgia (GMT-02:00)", value: "GMT-02:00" },
  { name: "America/Scoresbysund (GMT-01:00)", value: "GMT-01:00" },
  { name: "Atlantic/Azores (GMT-01:00)", value: "GMT-01:00" },
  { name: "Atlantic/Cape_Verde (GMT-01:00)", value: "GMT-01:00" },
  { name: "Africa/Abidjan (GMT+00:00)", value: "GMT+00:00" },
  { name: "Africa/Accra (GMT+00:00)", value: "GMT+00:00" },
  { name: "Africa/Bamako (GMT+00:00)", value: "GMT+00:00" },
  { name: "Africa/Banjul (GMT+00:00)", value: "GMT+00:00" },
  { name: "Africa/Bissau (GMT+00:00)", value: "GMT+00:00" },
  { name: "Africa/Casablanca (GMT+00:00)", value: "GMT+00:00" },
  { name: "Africa/Conakry (GMT+00:00)", value: "GMT+00:00" },
  { name: "Africa/Dakar (GMT+00:00)", value: "GMT+00:00" },
  { name: "Africa/El_Aaiun (GMT+00:00)", value: "GMT+00:00" },
  { name: "Africa/Freetown (GMT+00:00)", value: "GMT+00:00" },
  { name: "Africa/Lome (GMT+00:00)", value: "GMT+00:00" },
  { name: "Africa/Monrovia (GMT+00:00)", value: "GMT+00:00" },
  { name: "Africa/Nouakchott (GMT+00:00)", value: "GMT+00:00" },
  { name: "Africa/Ouagadougou (GMT+00:00)", value: "GMT+00:00" },
  { name: "Africa/Sao_Tome (GMT+00:00)", value: "GMT+00:00" },
  { name: "America/Danmarkshavn (GMT+00:00)", value: "GMT+00:00" },
  { name: "Antarctica/Troll (GMT+00:00)", value: "GMT+00:00" },
  { name: "Atlantic/Canary (GMT+00:00)", value: "GMT+00:00" },
  { name: "Atlantic/Faroe (GMT+00:00)", value: "GMT+00:00" },
  { name: "Atlantic/Madeira (GMT+00:00)", value: "GMT+00:00" },
  { name: "Atlantic/Reykjavik (GMT+00:00)", value: "GMT+00:00" },
  { name: "Atlantic/St_Helena (GMT+00:00)", value: "GMT+00:00" },
  { name: "Europe/Dublin (GMT+00:00)", value: "GMT+00:00" },
  { name: "Europe/Guernsey (GMT+00:00)", value: "GMT+00:00" },
  { name: "Europe/Isle_of_Man (GMT+00:00)", value: "GMT+00:00" },
  { name: "Europe/Jersey (GMT+00:00)", value: "GMT+00:00" },
  { name: "Europe/Lisbon (GMT+00:00)", value: "GMT+00:00" },
  { name: "Europe/London (GMT+00:00)", value: "GMT+00:00" },
  { name: "Pacific/Bougainville (GMT+11:00)", value: "GMT+11:00" },
  { name: "Pacific/Efate (GMT+11:00)", value: "GMT+11:00" },
  { name: "Pacific/Guadalcanal (GMT+11:00)", value: "GMT+11:00" },
  { name: "Pacific/Kosrae (GMT+11:00)", value: "GMT+11:00" },
  { name: "Pacific/Norfolk (GMT+11:00)", value: "GMT+11:00" },
  { name: "Pacific/Noumea (GMT+11:00)", value: "GMT+11:00" },
  { name: "Pacific/Pohnpei (GMT+11:00)", value: "GMT+11:00" },
  { name: "Antarctica/McMurdo (GMT+12:00)", value: "GMT+12:00" },
  { name: "Asia/Anadyr (GMT+12:00)", value: "GMT+12:00" },
  { name: "Asia/Kamchatka (GMT+12:00)", value: "GMT+12:00" },
  { name: "Pacific/Auckland (GMT+12:00)", value: "GMT+12:00" },
  { name: "Pacific/Fiji (GMT+12:00)", value: "GMT+12:00" },
  { name: "Pacific/Funafuti (GMT+12:00)", value: "GMT+12:00" },
  { name: "Pacific/Kwajalein (GMT+12:00)", value: "GMT+12:00" },
  { name: "Pacific/Majuro (GMT+12:00)", value: "GMT+12:00" },
  { name: "Pacific/Nauru (GMT+12:00)", value: "GMT+12:00" },
  { name: "Pacific/Tarawa (GMT+12:00)", value: "GMT+12:00" },
  { name: "Pacific/Wake (GMT+12:00)", value: "GMT+12:00" },
  { name: "Pacific/Wallis (GMT+12:00)", value: "GMT+12:00" },
  { name: "Pacific/Chatham (GMT+12:00)", value: "GMT+12:45" },
  { name: "Pacific/Apia (GMT+13:00)", value: "GMT+13:00" },
  { name: "Pacific/Enderbury (GMT+13:00)", value: "GMT+13:00" },
  { name: "Pacific/Fakaofo (GMT+13:00)", value: "GMT+13:00" },
  { name: "Pacific/Tongatapu (GMT+13:00)", value: "GMT+13:00" },
  { name: "Pacific/Kiritimati (GMT+14:00)", value: "GMT+14:00" },
  { name: "Africa/Freetown (GMT+00:00)", value: "GMT+00:00" },
  { name: "Africa/Lome (GMT+00:00)", value: "GMT+00:00" },
  { name: "Africa/Monrovia (GMT+00:00)", value: "GMT+00:00" },
  { name: "Africa/Nouakchott (GMT+00:00)", value: "GMT+00:00" },
  { name: "Africa/Ouagadougou (GMT+00:00)", value: "GMT+00:00" },
  { name: "Africa/Sao_Tome (GMT+00:00)", value: "GMT+00:00" },
  { name: "America/Danmarkshavn (GMT+00:00)", value: "GMT+00:00" },
  { name: "Antarctica/Troll (GMT+00:00)", value: "GMT+00:00" },
  { name: "Atlantic/Canary (GMT+00:00)", value: "GMT+00:00" },
  { name: "Atlantic/Faroe (GMT+00:00)", value: "GMT+00:00" },
  { name: "Atlantic/Madeira (GMT+00:00)", value: "GMT+00:00" },
  { name: "Atlantic/Reykjavik (GMT+00:00)", value: "GMT+00:00" },
  { name: "Atlantic/St_Helena (GMT+00:00)", value: "GMT+00:00" },
  { name: "Europe/Dublin (GMT+00:00)", value: "GMT+00:00" },
  { name: "Europe/Guernsey (GMT+00:00)", value: "GMT+00:00" },
  { name: "Europe/Isle_of_Man (GMT+00:00)", value: "GMT+00:00" },
  { name: "Europe/Jersey (GMT+00:00)", value: "GMT+00:00" },
  { name: "Europe/Lisbon (GMT+00:00)", value: "GMT+00:00" },
  { name: "Europe/London (GMT+00:00)", value: "GMT+00:00" },
  { name: "Africa/Algiers (GMT+01:00)", value: "GMT+01:00" },
  { name: "Africa/Bangui (GMT+01:00)", value: "GMT+01:00" },
  { name: "Africa/Brazzaville (GMT+01:00)", value: "GMT+01:00" },
  { name: "Africa/Ceuta (GMT+01:00)", value: "GMT+01:00" },
  { name: "Africa/Douala (GMT+01:00)", value: "GMT+01:00" },
  { name: "Africa/Kinshasa (GMT+01:00)", value: "GMT+01:00" },
  { name: "Africa/Lagos (GMT+01:00)", value: "GMT+01:00" },
  { name: "Africa/Libreville (GMT+01:00)", value: "GMT+01:00" },
  { name: "Africa/Luanda (GMT+01:00)", value: "GMT+01:00" },
  { name: "Africa/Malabo (GMT+01:00)", value: "GMT+01:00" },
  { name: "Africa/Ndjamena (GMT+01:00)", value: "GMT+01:00" },
  { name: "Africa/Niamey (GMT+01:00)", value: "GMT+01:00" },
  { name: "Africa/Porto-Novo (GMT+01:00)", value: "GMT+01:00" },
  { name: "Africa/Tunis (GMT+01:00)", value: "GMT+01:00" },
  { name: "Africa/Windhoek (GMT+01:00)", value: "GMT+01:00" },
  { name: "Arctic/Longyearbyen (GMT+01:00)", value: "GMT+01:00" },
  { name: "Europe/Amsterdam (GMT+01:00)", value: "GMT+01:00" },
  { name: "Europe/Andorra (GMT+01:00)", value: "GMT+01:00" },
  { name: "Europe/Belgrade (GMT+01:00)", value: "GMT+01:00" },
  { name: "Europe/Berlin (GMT+01:00)", value: "GMT+01:00" },
  { name: "Europe/Bratislava (GMT+01:00)", value: "GMT+01:00" },
  { name: "Europe/Brussels (GMT+01:00)", value: "GMT+01:00" },
  { name: "Europe/Budapest (GMT+01:00)", value: "GMT+01:00" },
  { name: "Europe/Copenhagen (GMT+01:00)", value: "GMT+01:00" },
  { name: "Europe/Gibraltar (GMT+01:00)", value: "GMT+01:00" },
  { name: "Europe/Ljubljana (GMT+01:00)", value: "GMT+01:00" },
  { name: "Europe/Luxembourg (GMT+01:00)", value: "GMT+01:00" },
  { name: "Europe/Madrid (GMT+01:00)", value: "GMT+01:00" },
  { name: "Europe/Malta (GMT+01:00)", value: "GMT+01:00" },
  { name: "Europe/Monaco (GMT+01:00)", value: "GMT+01:00" },
  { name: "Europe/Oslo (GMT+01:00)", value: "GMT+01:00" },
  { name: "Europe/Podgorica (GMT+01:00)", value: "GMT+01:00" },
  { name: "Europe/Prague (GMT+01:00)", value: "GMT+01:00" },
  { name: "Europe/Rome (GMT+01:00)", value: "GMT+01:00" },
  { name: "Europe/San_Marino (GMT+01:00)", value: "GMT+01:00" },
  { name: "Europe/Sarajevo (GMT+01:00)", value: "GMT+01:00" },
  { name: "Europe/Skopje (GMT+01:00)", value: "GMT+01:00" },
  { name: "Europe/Stockholm (GMT+01:00)", value: "GMT+01:00" },
  { name: "Europe/Tirane (GMT+01:00)", value: "GMT+01:00" },
  { name: "Europe/Vaduz (GMT+01:00)", value: "GMT+01:00" },
  { name: "Europe/Vatican (GMT+01:00)", value: "GMT+01:00" },
  { name: "Europe/Vienna (GMT+01:00)", value: "GMT+01:00" },
  { name: "Europe/Warsaw (GMT+01:00)", value: "GMT+01:00" },
  { name: "Europe/Zagreb (GMT+01:00)", value: "GMT+01:00" },
  { name: "Europe/Zurich (GMT+01:00)", value: "GMT+01:00" },
  { name: "Africa/Blantyre (GMT+02:00)", value: "GMT+02:00" },
  { name: "Africa/Bujumbura (GMT+02:00)", value: "GMT+02:00" },
  { name: "Africa/Cairo (GMT+02:00)", value: "GMT+02:00" },
  { name: "Africa/Gaborone (GMT+02:00)", value: "GMT+02:00" },
  { name: "Africa/Harare (GMT+02:00)", value: "GMT+02:00" },
  { name: "Africa/Johannesburg (GMT+02:00)", value: "GMT+02:00" },
  { name: "Africa/Juba (GMT+02:00)", value: "GMT+02:00" },
  { name: "Africa/Khartoum (GMT+02:00)", value: "GMT+02:00" },
  { name: "Africa/Lubumbashi (GMT+02:00)", value: "GMT+02:00" },
  { name: "Africa/Lusaka (GMT+02:00)", value: "GMT+02:00" },
  { name: "Africa/Maputo (GMT+02:00)", value: "GMT+02:00" },
  { name: "Africa/Maseru (GMT+02:00)", value: "GMT+02:00" },
  { name: "Africa/Mbabane (GMT+02:00)", value: "GMT+02:00" },
  { name: "Africa/Tripoli (GMT+02:00)", value: "GMT+02:00" },
  { name: "Asia/Amman (GMT+02:00)", value: "GMT+02:00" },
  { name: "Asia/Beirut (GMT+02:00)", value: "GMT+02:00" },
  { name: "Asia/Damascus (GMT+02:00)", value: "GMT+02:00" },
  { name: "Asia/Famagusta (GMT+02:00)", value: "GMT+02:00" },
  { name: "Asia/Gaza (GMT+02:00)", value: "GMT+02:00" },
  { name: "Asia/Hebron (GMT+02:00)", value: "GMT+02:00" },
  { name: "Asia/Jerusalem (GMT+02:00)", value: "GMT+02:00" },
  { name: "Asia/Nicosia (GMT+02:00)", value: "GMT+02:00" },
  { name: "Europe/Athens (GMT+02:00)", value: "GMT+02:00" },
  { name: "Europe/Bucharest (GMT+02:00)", value: "GMT+02:00" },
  { name: "Europe/Chisinau (GMT+02:00)", value: "GMT+02:00" },
  { name: "Europe/Helsinki (GMT+02:00)", value: "GMT+02:00" },
  { name: "Europe/Kaliningrad (GMT+02:00)", value: "GMT+02:00" },
  { name: "Europe/Kyiv (GMT+02:00)", value: "GMT+02:00" },
  { name: "Europe/Mariehamn (GMT+02:00)", value: "GMT+02:00" },
  { name: "Europe/Riga (GMT+02:00)", value: "GMT+02:00" },
  { name: "Europe/Sofia (GMT+02:00)", value: "GMT+02:00" },
  { name: "Europe/Tallinn (GMT+02:00)", value: "GMT+02:00" },
  { name: "Europe/Uzhgorod (GMT+02:00)", value: "GMT+02:00" },
  { name: "Europe/Vilnius (GMT+02:00)", value: "GMT+02:00" },
  { name: "Europe/Zaporozhye (GMT+02:00)", value: "GMT+02:00" },
  { name: "Africa/Addis_Ababa (GMT+03:00)", value: "GMT+03:00" },
  { name: "Africa/Asmara (GMT+03:00)", value: "GMT+03:00" },
  { name: "Africa/Dar_es_Salaam (GMT+03:00)", value: "GMT+03:00" },
  { name: "Africa/Djibouti (GMT+03:00)", value: "GMT+03:00" },
  { name: "Africa/Kampala (GMT+03:00)", value: "GMT+03:00" },
  { name: "Africa/Mogadishu (GMT+03:00)", value: "GMT+03:00" },
  { name: "Africa/Nairobi (GMT+03:00)", value: "GMT+03:00" },
  { name: "Antarctica/Syowa (GMT+03:00)", value: "GMT+03:00" },
  { name: "Asia/Aden (GMT+03:00)", value: "GMT+03:00" },
  { name: "Asia/Baghdad (GMT+03:00)", value: "GMT+03:00" },
  { name: "Asia/Bahrain (GMT+03:00)", value: "GMT+03:00" },
  { name: "Asia/Kuwait (GMT+03:00)", value: "GMT+03:00" },
  { name: "Asia/Qatar (GMT+03:00)", value: "GMT+03:00" },
  { name: "Asia/Riyadh (GMT+03:00)", value: "GMT+03:00" },
  { name: "Europe/Istanbul (GMT+03:00)", value: "GMT+03:00" },
  { name: "Europe/Kirov (GMT+03:00)", value: "GMT+03:00" },
  { name: "Europe/Minsk (GMT+03:00)", value: "GMT+03:00" },
  { name: "Europe/Moscow (GMT+03:00)", value: "GMT+03:00" },
  { name: "Europe/Simferopol (GMT+03:00)", value: "GMT+03:00" },
  { name: "Europe/Volgograd (GMT+03:00)", value: "GMT+03:00" },
  { name: "Indian/Antananarivo (GMT+03:00)", value: "GMT+03:00" },
  { name: "Indian/Comoro (GMT+03:00)", value: "GMT+03:00" },
  { name: "Indian/Mayotte (GMT+03:00)", value: "GMT+03:00" },
  { name: "Asia/Tehran (GMT+03:00)", value: "GMT+03:30" },
  { name: "Asia/Baku (GMT+04:00)", value: "GMT+04:00" },
  { name: "Asia/Dubai (GMT+04:00)", value: "GMT+04:00" },
  { name: "Asia/Muscat (GMT+04:00)", value: "GMT+04:00" },
  { name: "Asia/Tbilisi (GMT+04:00)", value: "GMT+04:00" },
  { name: "Asia/Yerevan (GMT+04:00)", value: "GMT+04:00" },
  { name: "Europe/Astrakhan (GMT+04:00)", value: "GMT+04:00" },
  { name: "Europe/Samara (GMT+04:00)", value: "GMT+04:00" },
  { name: "Europe/Saratov (GMT+04:00)", value: "GMT+04:00" },
  { name: "Europe/Ulyanovsk (GMT+04:00)", value: "GMT+04:00" },
  { name: "Indian/Mahe (GMT+04:00)", value: "GMT+04:00" },
  { name: "Indian/Mauritius (GMT+04:00)", value: "GMT+04:00" },
  { name: "Indian/Reunion (GMT+04:00)", value: "GMT+04:00" },
  { name: "Asia/Kabul (GMT+04:30)", value: "GMT+04:30" },
  { name: "Antarctica/Mawson (GMT+05:00)", value: "GMT+05:00" },
  { name: "Asia/Aqtau (GMT+05:00)", value: "GMT+05:00" },
  { name: "Asia/Aqtobe (GMT+05:00)", value: "GMT+05:00" },
  { name: "Asia/Ashgabat (GMT+05:00)", value: "GMT+05:00" },
  { name: "Asia/Atyrau (GMT+05:00)", value: "GMT+05:00" },
  { name: "Asia/Dushanbe (GMT+05:00)", value: "GMT+05:00" },
  { name: "Asia/Karachi (GMT+05:00)", value: "GMT+05:00" },
  { name: "Asia/Oral (GMT+05:00)", value: "GMT+05:00" },
  { name: "Asia/Qyzylorda (GMT+05:00)", value: "GMT+05:00" },
  { name: "Asia/Samarkand (GMT+05:00)", value: "GMT+05:00" },
  { name: "Asia/Tashkent (GMT+05:00)", value: "GMT+05:00" },
  { name: "Asia/Yekaterinburg (GMT+05:00)", value: "GMT+05:00" },
  { name: "Indian/Kerguelen (GMT+05:00)", value: "GMT+05:00" },
  { name: "Indian/Maldives (GMT+05:00)", value: "GMT+05:00" },
  { name: "Asia/Colombo (GMT+05:30)", value: "GMT+05:30" },
  { name: "Asia/Kolkata (GMT+05:30)", value: "GMT+05:30" },
  { name: "Antarctica/Vostok (GMT+06:00)", value: "GMT+06:00" },
  { name: "Asia/Almaty (GMT+06:00)", value: "GMT+06:00" },
  { name: "Asia/Bishkek (GMT+06:00)", value: "GMT+06:00" },
  { name: "Asia/Dhaka (GMT+06:00)", value: "GMT+06:00" },
  { name: "Asia/Omsk (GMT+06:00)", value: "GMT+06:00" },
  { name: "Asia/Qostanay (GMT+06:00)", value: "GMT+06:00" },
  { name: "Asia/Thimphu (GMT+06:00)", value: "GMT+06:00" },
  { name: "Asia/Urumqi (GMT+06:00)", value: "GMT+06:00" },
  { name: "Indian/Chagos (GMT+06:00)", value: "GMT+06:00" },
  { name: "Asia/Yangon (GMT+06:30)", value: "GMT+06:30" },
  { name: "Indian/Cocos (GMT+06:30)", value: "GMT+06:30" },
  { name: "Antarctica/Davis (GMT+07:00)", value: "GMT+07:00" },
  { name: "Asia/Bangkok (GMT+07:00)", value: "GMT+07:00" },
  { name: "Asia/Barnaul (GMT+07:00)", value: "GMT+07:00" },
  { name: "Asia/Hovd (GMT+07:00)", value: "GMT+07:00" },
  { name: "Asia/Ho_Chi_Minh (GMT+07:00)", value: "GMT+07:00" },
  { name: "Asia/Jakarta (GMT+07:00)", value: "GMT+07:00" },
  { name: "Asia/Krasnoyarsk (GMT+07:00)", value: "GMT+07:00" },
  { name: "Asia/Novokuznetsk (GMT+07:00)", value: "GMT+07:00" },
  { name: "Asia/Novosibirsk (GMT+07:00)", value: "GMT+07:00" },
  { name: "Asia/Phnom_Penh (GMT+07:00)", value: "GMT+07:00" },
  { name: "Asia/Pontianak (GMT+07:00)", value: "GMT+07:00" },
  { name: "Asia/Tomsk (GMT+07:00)", value: "GMT+07:00" },
  { name: "Asia/Vientiane (GMT+07:00)", value: "GMT+07:00" },
  { name: "Indian/Christmas (GMT+07:00)", value: "GMT+07:00" },
  { name: "Asia/Brunei (GMT+08:00)", value: "GMT+08:00" },
  { name: "Asia/Choibalsan (GMT+08:00)", value: "GMT+08:00" },
  { name: "Asia/Hong_Kong (GMT+08:00)", value: "GMT+08:00" },
  { name: "Asia/Irkutsk (GMT+08:00)", value: "GMT+08:00" },
  { name: "Asia/Kuala_Lumpur (GMT+08:00)", value: "GMT+08:00" },
  { name: "Asia/Kuching (GMT+08:00)", value: "GMT+08:00" },
  { name: "Asia/Macau (GMT+08:00)", value: "GMT+08:00" },
  { name: "Asia/Makassar (GMT+08:00)", value: "GMT+08:00" },
  { name: "Asia/Manila (GMT+08:00)", value: "GMT+08:00" },
  { name: "Asia/Shanghai (GMT+08:00)", value: "GMT+08:00" },
  { name: "Asia/Singapore (GMT+08:00)", value: "GMT+08:00" },
  { name: "Asia/Taipei (GMT+08:00)", value: "GMT+08:00" },
  { name: "Asia/Ulaanbaatar (GMT+08:00)", value: "GMT+08:00" },
  { name: "Australia/Perth (GMT+08:00)", value: "GMT+08:00" },
  { name: "Australia/Eucla (GMT+08:45)", value: "GMT+08:45" },
  { name: "Asia/Chita (GMT+09:00)", value: "GMT+09:00" },
  { name: "Asia/Dili (GMT+09:00)", value: "GMT+09:00" },
  { name: "Asia/Jayapura (GMT+09:00)", value: "GMT+09:00" },
  { name: "Asia/Khandyga (GMT+09:00)", value: "GMT+09:00" },
  { name: "Asia/Pyongyang (GMT+09:00)", value: "GMT+09:00" },
  { name: "Asia/Seoul (GMT+09:00)", value: "GMT+09:00" },
  { name: "Asia/Tokyo (GMT+09:00)", value: "GMT+09:00" },
  { name: "Asia/Yakutsk (GMT+09:00)", value: "GMT+09:00" },
  { name: "Pacific/Palau (GMT+09:00)", value: "GMT+09:00" },
  { name: "Australia/Adelaide (GMT+09:30)", value: "GMT+09:30" },
  { name: "Australia/Broken_Hill (GMT+09:30)", value: "GMT+09:30" },
  { name: "Australia/Darwin (GMT+09:30)", value: "GMT+09:30" },
  { name: "Antarctica/DumontDUrville (GMT+10:00)", value: "GMT+10:00" },
  { name: "Antarctica/Macquarie (GMT+10:00)", value: "GMT+10:00" },
  { name: "Asia/Ust-Nera (GMT+10:00)", value: "GMT+10:00" },
  { name: "Asia/Vladivostok (GMT+10:00)", value: "GMT+10:00" },
  { name: "Australia/Brisbane (GMT+10:00)", value: "GMT+10:00" },
  { name: "Australia/Currie (GMT+10:00)", value: "GMT+10:00" },
  { name: "Australia/Hobart (GMT+10:00)", value: "GMT+10:00" },
  { name: "Australia/Lindeman (GMT+10:00)", value: "GMT+10:00" },
  { name: "Australia/Melbourne (GMT+10:00)", value: "GMT+10:00" },
  { name: "Australia/Sydney (GMT+10:00)", value: "GMT+10:00" },
  { name: "Pacific/Chuuk (GMT+10:00)", value: "GMT+10:00" },
  { name: "Pacific/GuamVillage (GMT+10:00)", value: "GMT+10:00" },
  { name: "Pacific/Port_Moresby (GMT+10:00)", value: "GMT+10:00" },
  { name: "Pacific/Saipan (GMT+10:00)", value: "GMT+10:00" },
  { name: "Australia/Lord_Howe (GMT+10:30)", value: "GMT+10:30" },
  { name: "Antarctica/Casey (GMT+11:00)", value: "GMT+11:00" },
  { name: "Asia/Magadan (GMT+11:00)", value: "GMT+11:00" },
  { name: "Asia/Sakhalin (GMT+11:00)", value: "GMT+11:00" },
  { name: "Asia/Srednekolymsk (GMT+11:00)", value: "GMT+11:00" },
  { name: "Pacific/Bougainville (GMT+11:00)", value: "GMT+11:00" },
  { name: "Pacific/Efate (GMT+11:00)", value: "GMT+11:00" },
  { name: "Pacific/Guadalcanal (GMT+11:00)", value: "GMT+11:00" },
  { name: "Pacific/Kosrae (GMT+11:00)", value: "GMT+11:00" },
  { name: "Pacific/Norfolk (GMT+11:00)", value: "GMT+11:00" },
  { name: "Pacific/Noumea (GMT+11:00)", value: "GMT+11:00" },
  { name: "Pacific/Pohnpei (GMT+11:00)", value: "GMT+11:00" },
  { name: "Antarctica/McMurdo (GMT+12:00)", value: "GMT+12:00" },
  { name: "Asia/Anadyr (GMT+12:00)", value: "GMT+12:00" },
  { name: "Asia/Kamchatka (GMT+12:00)", value: "GMT+12:00" },
  { name: "Pacific/Auckland (GMT+12:00)", value: "GMT+12:00" },
  { name: "Pacific/Fiji (GMT+12:00)", value: "GMT+12:00" },
  { name: "Pacific/Funafuti (GMT+12:00)", value: "GMT+12:00" },
  { name: "Pacific/Kwajalein (GMT+12:00)", value: "GMT+12:00" },
  { name: "Pacific/Majuro (GMT+12:00)", value: "GMT+12:00" },
  { name: "Pacific/Nauru (GMT+12:00)", value: "GMT+12:00" },
  { name: "Pacific/Tarawa (GMT+12:00)", value: "GMT+12:00" },
  { name: "Pacific/Wake (GMT+12:00)", value: "GMT+12:00" },
  { name: "Pacific/Wallis (GMT+12:00)", value: "GMT+12:00" },
  { name: "Pacific/Chatham (GMT+12:45)", value: "GMT+12:45" },
  { name: "Pacific/Apia (GMT+13:00)", value: "GMT+13:00" },
  { name: "Pacific/Enderbury (GMT+13:00)", value: "GMT+13:00" },
  { name: "Pacific/Fakaofo (GMT+13:00)", value: "GMT+13:00" },
  { name: "Pacific/Tongatapu (GMT+13:00)", value: "GMT+13:00" },
  { name: "Pacific/Kiritimati (GMT+14:00)", value: "GMT+14:00" },
];

export const riskStatus = [
  { name: "Identified", value: "Identified" },
  { name: "Opened", value: "Opened" },
  { name: "Monitoring", value: "Monitoring" },
  { name: "Escalated", value: "Escalated" },
  { name: "Impacted", value: "Impacted" },
  { name: "Closed", value: "Closed" },
];

export const changeRequestStatus = [
  { name: "Initiating", value: "Initiating" },
  { name: "Pending", value: "Pending" },
  { name: "Approved", value: "Approved" },
  { name: "Rejected", value: "Rejected" },
  { name: "Deferred", value: "Deferred" },
  { name: "Executing", value: "Executing" },
  { name: "Implemented", value: "Implemented" },
  { name: "Withdraw", value: "Withdraw" },
];

export const changeRequestTypes = [
  { name: "Scope", value: "Scope" },
  { name: "Schedule", value: "Schedule" },
  { name: "Budget", value: "Budget" },
  { name: "Quality", value: "Quality" },
  { name: "Resource", value: "Resource" },
];

export const riskCategoryOptions = [
  { name: "Scope", value: 0 },
  { name: "Schedule", value: 1 },
  { name: "Budget", value: 2 },
  { name: "Quality", value: 3 },
  { name: "Resource", value: 4 },
];

export const changeRequestPriorities = [
  { name: "Low", value: "Low" },
  { name: "Moderate", value: "Moderate" },
  { name: "High", value: "High" },
];

export const reminderPriorityOptions = [
  { name: "Low", value: 0 },
  { name: "Moderate", value: 1 },
  { name: "High", value: 2 },
];

export const riskStatusOptions = [
  { name: "Identified", value: "Identified" },
  { name: "Opened", value: "Opened" },
  { name: "Monitoring", value: "Monitoring" },
  { name: "Escalated", value: "Escalated" },
  { name: "Impacted", value: "Impacted" },
  { name: "Closed", value: "Closed" }
];

export const RiskStatusOptions = [
  { name: "Identified", value: 0 },
  { name: "Opened", value: 1 },
  { name: "Monitoring", value: 2 },
  { name: "Escalated", value: 3 },
  { name: "Impacted", value: 4 },
  { name: "Closed", value: 5 }
];

export const riskProbability = [
  { name: "1", value: 1 },
  { name: "2", value: 2 },
  { name: "3", value: 3 },
  { name: "4", value: 4 },
  { name: "5", value: 5 },
];

export const riskImpact = [
  { name: "1", value: 1 },
  { name: "2", value: 2 },
  { name: "3", value: 3 },
  { name: "4", value: 4 },
  { name: "5", value: 5 },
];
export const riskTrendingStatusOptions = [
  { name: "On Target", value: 0 },
  { name: "Monitoring", value: 1 },
  { name: "Slipping", value: 2 }
];
export const taskDependencyStatus = [
  { name: "Finish To Start", value: "Finish To Start" },
  { name: "Start To Start", value: "Start To Start" },
];

export const taskStatusOptions = [
  { name: "In Progress", value: 0 },
  { name: "Monitoring", value: 1 },
  { name: "Slipping", value: 2 },
  { name: "Completed", value: 3 },
  { name: "On Hold", value: 4 },
];

export const taskPriorityOptions = [
  { name: "Low", value: 0 },
  { name: "Moderate", value: 1 },
  { name: "High", value: 2 }
];

export const scopePriorityOptions = [
  { name: "Low", value: 0 },
  { name: "Moderate", value: 1 },
  { name: "High", value: 2 }
];

export const projectStatus = [
  { name: "On Target", value: 0 },
  { name: "Monitoring", value: 1 },
  { name: "Slipping", value: 2 },
  { name: "Completed", value: 3 },
  { name: "On Hold", value: 4 },
];

export const projectTypes = [
  { name: "Fixed Price", value: 0 },
  { name: "T&M", value: 1 },
];

export const ScopeStatus = [
  { name: "On Target", value: 0 },
  { name: "Monitoring", value: 1 },
  { name: "Slipping", value: 2 },
  { name: "Completed", value: 3 },
];

export const MilestoneStatusOptions = [
  { name: "On Target", value: 0 },
  { name: "Monitoring", value: 1 },
  { name: "Slipping", value: 2 },
  { name: "Completed", value: 3 },
];

export const MilestonePriorityOptions = [
  { name: "Low", value: 0 },
  { name: "Moderate", value: 1 },
  { name: "High", value: 2 },
  { name: "Critical", value: 3 }
];

export const exportPeriods = [
  { name: "Last Week", value: 0 },
  { name: "Last Month", value: 1 },
  { name: "All", value: 2 },
  { name: "Custom", value: 3 },
];

export const exportTypes = [
  { name: "Grouped", value: 0 },
  { name: "Detailed", value: 1 }
];

export const exportFormats = [
  { name: "Csv", value: 0 },
  { name: "Excel", value: 1 }
];

export const exportCategories = [
  { name: "Budgets", value: 0 },
  { name: "CashFlow", value: 1 },
  { name: "PurchaseOrders", value: 2 },
  { name: "Invoices", value: 3 },
];

export const HealthKpiStatus = [
  { name: "On Target", value: 0, color: "#0B6A0B" },
  { name: "Monitoring", value: 1, color: "#EAA300" },
  { name: "Slipping", value: 2, color: "#C50F1F" },
];

export const communicationCategoryOptions = [
  { name: "Initiating", value: 0 },
  { name: "Planning", value: 1 },
  { name: "Monitoring", value: 2 },
  { name: "Governance", value: 3 },
  { name: "Steering CO", value: 4 },
  { name: "Cost Management", value: 5 },
  { name: "Risk Management", value: 6 },
  { name: "Resource Management", value: 7 },
  { name: "Closing", value: 8 },
];

export const purchaseOrderStatusOptions = [
  { name: "Open", value: 0 },
  { name: "Closed", value: 1 },
];

export const cashflowLineCategoryOptions = [
  { name: "Custom", value: 0 },
  { name: "Labor", value: 1 },
  { name: "Hardware", value: 2 },
  { name: "Software", value: 3 },
  { name: "Licenses", value: 4 },
  { name: "One Time Charges", value: 5 },
  { name: "Recurrent Charges", value: 6 },
  { name: "Travel & Expenses", value: 7 },
  { name: "Representations", value: 8 },
];

export const monthsShortNames = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];

export function getRiskSeverityColorClass(severity) {
  if (severity >= 17) return "AtRisk";
  if (severity >= 8) return "OffTrack";
  if (severity >= 0) return "OnTrack";
  return "";
}

export function getRiskSeverityStatus(severity) {
  if (severity >= 17) return "High";
  if (severity >= 8) return "Moderate";
  if (severity >= 0) return "Low";
  return "";
}

export const getRiskStatus = (status) => {
  switch (status) {
    case 1:
      return "Opened";
    case 2:
      return "Monitoring";
    case 3:
      return "Escalated";
    case 4:
      return "Impacted";
    case 5:
      return "Closed";
    default:
      return "Identified";
  }
};

export const getRiskType = (type) => {
  switch (type) {
    case 1:
      return "Schedule";
    case 2:
      return "Budget";
    case 3:
      return "Quality";
    case 4:
      return "Resource";
    default:
      return "Scope";
  }
};

export function getRiskTrendingColorClass(status) {
  switch (status) {
    case 1:
      return "OffTrack";
    case 2:
      return "AtRisk";
    default:
      return "OnTrack";
  }
}

export function getLabelByType(type, index) {
  const enums = {
    scope: ScopeStatus,
    project: projectStatus,
    projectType: projectTypes,
    exportPeriod: exportPeriods,
    exportType: exportTypes,
    exportFormat: exportFormats,
    exportCategories: exportCategories,
    health: HealthKpiStatus,
    communicationCategory: communicationCategoryOptions,
    purchaseOrderStatus: purchaseOrderStatusOptions,
    cashflowLineCategory: cashflowLineCategoryOptions,
    taskStatus: taskStatusOptions,
    taskPriority: taskPriorityOptions,
    scopePriority: scopePriorityOptions,
    milestoneStatus: MilestoneStatusOptions,
    milestonePriority: MilestonePriorityOptions,
    reminderPriority: reminderPriorityOptions,
    riskTrending: riskTrendingStatusOptions,
  };

  return enums[type]?.[index]?.name ?? "";
}

export const getMilestonePriorityPillClass = (priority) => {
  switch (priority) {
    case 0:
      return "pill OnTrack";
    case 1:
      return "pill OffTrack";
    case 2:
    case 3:
      return "pill AtRisk";
    default:
      return "";
  }
};

export const getMilestoneStatusPillClass = (status) => {
  switch (status) {
    case 0:
      return "pill OnTrack";
    case 1:
      return "pill OffTrack";
    case 2:
      return "pill AtRisk";
    case 3:
      return "pill Completed";
    default:
      return "";
  }
};

export const getTaskPriorityPillClass = (priority) => {
  switch (priority) {
    case 0:
      return "pill OnTrack";
    case 1:
      return "pill OffTrack";
    case 2:
    case 3:
      return "pill AtRisk";
    default:
      return "";
  }
};

export const getTaskStatusPillClass = (status) => {
  switch (status) {
    case 0:
      return "pill OnTrack";
    case 1:
      return "pill OffTrack";
    case 2:
      return "pill AtRisk";
    case 3:
      return "pill Completed";
    case 4:
      return "pill OnHold";
    default:
      return "";
  }
};

export const getProjectStatusPillClass = (status) => {
  switch (status) {
    case 0:
      return "pill OnTrack";
    case 1:
      return "pill OffTrack";
    case 2:
      return "pill AtRisk";
    case 3:
      return "pill Completed";
    case 4:
      return "pill OnHold";
    default:
      return "";
  }
};

export const getProjectStatusTrackPillClass = (status) => {
  switch (status) {
    case 0:
      return "TrackPill OnTrack";
    case 1:
      return "TrackPill OffTrack";
    case 2:
      return "TrackPill AtRisk";
    case 3:
      return "TrackPill Completed";
    case 4:
      return "TrackPill OnHold";
    default:
      return "";
  }
};

export const TimelineTaskIcons = {
  3: (color = null) => (
    <div className="flex items-center h-[12px] w-[12px]">
      <CompletedTaskIcon size={16} color={color} />
    </div>
  ),
  0: (color = null) => (
    <div className="flex items-center h-[12px] w-[12px]">
      <InProgressTaskIcon size={16} color={color} />
    </div>
  ),
  2: (color = null) => (
    <div className="flex items-center h-[12px] w-[12px]">
      <SlippingTaskIcon size={16} color={color} />
    </div>
  ),
  1: (color = null) => (
    <div className="flex items-center h-[12px] w-[12px]">
      <MonitoringTaskIcon size={16} color={color} />
    </div>
  ),
  4: () => (
    <div className="flex items-center h-[12px] w-[12px]">
      <OnHoldTaskIcon size={16} color="#AAAAAA" />
    </div>
  ),
};

export function getColorByType(type, index) {
  const enums = {
    health: HealthKpiStatus,
  };

  return enums[type]?.[index]?.color ?? "";
}

export const incrementTime = (timer) => {
  let startTime = `${timer.split(":")[0]}:00 ${timer.split(" ")[1]}`;
  let [time, modifier] = startTime.split(" ");
  let [hours, minutes] = time.split(":").map(Number);

  if (modifier === "PM" && hours !== 12) {
    hours += 12;
  } else if (modifier === "AM" && hours === 12) {
    hours = 0;
  }

  let date = new Date();
  date.setHours(hours);
  date.setMinutes(minutes);
  date.setSeconds(0);

  function incrementTimeOne() {
    date.setHours(date.getHours() + 1);
    return date.toLocaleTimeString("en-US", {
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    });
  }
  return incrementTimeOne();
};

export const tConverter = (time) => {
  let twelveTime = "";
  let t = time.split(":");
  if (Number(t[0]) == 0) {
    twelveTime = `12:${t[1]} AM`;
  } else if (Number(t[0]) > 12) {
    twelveTime =
      Number(t[0]) - 12 > 9
        ? `${Number(t[0]) - 12}:${t[1]} PM`
        : `0${Number(t[0]) - 12}:${t[1]} PM`;
  } else if (Number(t[0]) == 12) {
    twelveTime = `12:${t[1]} PM`;
  } else {
    twelveTime =
      Number(t[0]) - 12 > 9
        ? `${Number(t[0])}:${t[1]} AM`
        : `0${Number(t[0])}:${t[1]} AM`;
  }
  return twelveTime;
};

export const tConverterHeaderless = (timeoriginal) => {
  let time = timeoriginal.trim();
  if (time.length >= 5) {
    time = time.split(" ")[0];
  }
  if (time.split(":")[0] > 24) {
    time = `12:${time.split(":")[1]}`;
  }
  if (time.split(":")[1] > 59) {
    time = `${time.split(":")[0]}:00`;
  }
  let twelveTime = "";
  let t = time.split(":");
  if (Number(t[0]) == 0) {
    twelveTime = `12:${t[1]}`;
  } else if (Number(t[0]) > 12) {
    twelveTime =
      Number(t[0]) - 12 > 9
        ? `${Number(t[0]) - 12}:${t[1]}`
        : `0${Number(t[0]) - 12}:${t[1]}`;
  } else if (Number(t[0]) == 12) {
    twelveTime = `12:${t[1]}`;
  } else {
    twelveTime =
      Number(t[0]) - 12 > 9
        ? `${Number(t[0])}:${t[1]}`
        : `0${Number(t[0])}:${t[1]}`;
  }
  return `${time.split(":")[0] < 13 ? time : twelveTime} ${timeoriginal.trim().split(" ")[1]
    ? timeoriginal.split(" ")[1]
    : time.split(":")[0] >= 12
      ? "PM"
      : "AM"
    }`;
};

export const tConverter24 = (time) => {
  let twentyFourTime = "";
  const [timeValue, period] = time.split(" ");

  if (period.toUpperCase() === "AM") {
    if (timeValue === "12") {
      twentyFourTime = "00:00";
    } else {
      // Pad single-digit hours with a leading zero
      const hour = timeValue.padStart(2, "0");
      twentyFourTime = `${hour}:00`;
    }
  } else {
    // PM
    const hour = Number(timeValue);
    if (hour === 12) {
      twentyFourTime = "12:00";
    } else {
      // Add 12 to convert to 24-hour format, ensuring it's always two digits
      twentyFourTime = `${hour + 12}:00`;
    }
  }

  return twentyFourTime;
};

export const tFullConverter24 = (time) => {
  let twentyFourTime = "";
  if (time.split(" ")[1].toUpperCase() === "AM") {
    if (time.split(" ")[0].split(":")[0] == 12) {
      twentyFourTime = `00:${time.split(" ")[0].split(":")[1]}`;
    } else {
      twentyFourTime = `${time.split(" ")[0]}`;
    }
  } else {
    if (time.split(" ")[0].split(":")[0] == 12) {
      twentyFourTime = `12:${time.split(" ")[0].split(":")[1]}`;
    } else {
      twentyFourTime = `${Number(time.split(" ")[0].split(":")[0]) + 12}:${time.split(" ")[0].split(":")[1]
        }`;
    }
  }
  return twentyFourTime;
};


export const validate24h = (date) => {
  console.log("validate24h", date)
  let formatedDate = date;
  if (formatedDate.split(":")[0] >= 24) {
    formatedDate = `00:${formatedDate.split(":")[1]}`;
  }
  if (formatedDate.split(":")[1] >= 59) {
    formatedDate = `${formatedDate.split(":")[0]}:00`;
  }
  return formatedDate;
};

export const resetUtcDate = (date) => {
  return date && date.endsWith("Z") ? date.slice(0, -1) : date;
};

// Helper function to convert time to minutes for comparison
export function convertToMinutes(time) {
  const [hours, minutes] = time.split(":").map(Number);
  return hours * 60 + minutes;
}

// Calculate capacity from start time till midnight
export function calculateCapacityTillMidnight(startTime, endTime) {
  const startHours = Number(startTime.split(":")[0]);
  const startPeriod = startTime.split(" ")[1];

  // Convert to 24-hour format for calculation
  const hoursMap = {
    AM: startHours === 12 ? 0 : startHours,
    PM: startHours === 12 ? 12 : startHours + 12,
  };

  const startMinutes = hoursMap[startPeriod] * 60;
  const endMinutes = 24 * 60;

  return Number(((endMinutes - startMinutes) / 60).toFixed(2));
}

export function formatNumberToShortCurrency(
  value,
  currencySymbol,
  decimals = 2
) {
  const abs = Math.abs(value);
  let divisor = 1;
  let suffix = "";

  if (abs >= 1_000_000_000) {
    divisor = 1_000_000_000;
    suffix = "B";
  } else if (abs >= 1_000_000) {
    divisor = 1_000_000;
    suffix = "M";
  } else if (abs >= 1_000) {
    divisor = 1_000;
    suffix = "K";
  }

  const scaled = value / divisor;

  // Truncate instead of round
  const factor = Math.pow(10, decimals);
  const truncated = Math.trunc(scaled * factor) / factor;

  let result = suffix ? `${truncated}${suffix}` : `${value}`;

  // Remove trailing zeros and unnecessary decimals
  return `${currencySymbol} ${result.replace(/\.0+$|(\.\d*[1-9])0+$/, "$0")}`;
}

export function convertNameToEllipsis(value, maxLength) {
  if (!value) return "";
  if (value.length <= maxLength) return value;

  if (value.indexOf(" ") > 0) {
    let result = "";
    const words = value.split(" ");

    for (const word of words) {
      if ((result + word).length <= maxLength - 2) {
        result += (result ? " " : "") + word;
      } else {
        break;
      }
    }

    return result + "..";
  } else {
    return value.substring(0, maxLength - 2) + "..";
  }
}

export const calculatePageSize = () => {
  const rowHeight = 34;
  const topToolbarHeight = 56;
  const bottomToolbarHeight = 56;
  const availableHeight = window.innerHeight - topToolbarHeight - bottomToolbarHeight - 100;
  const pageSize = Math.floor(availableHeight / rowHeight);
  return pageSize;
};

// ── Reshuffle Request helpers ─────────────────────────────────────────────────
const MONTH_ABBR = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

export const formatMonthYear = (year, month) => {
  if (!year || !month) return "";
  return `${MONTH_ABBR[(month - 1) % 12]} ${year}`;
};

export const formatPeriodMonthYear = (startMonth, startYear, endMonth, endYear) => {
    const hasStart = startMonth && startYear;
    const hasEnd = endMonth && endYear;

    if (!hasStart && !hasEnd) {
        return "";
    }

    if (!hasStart) {
        return `${MONTH_ABBR[endMonth - 1]} ${endYear}`;
    }

    if (!hasEnd) {
        return `${MONTH_ABBR[startMonth - 1]} ${startYear}`;
    }

    const start = MONTH_ABBR[startMonth - 1];
    const end = MONTH_ABBR[endMonth - 1];

    if (startMonth === endMonth && startYear === endYear) {
        return `${start} ${startYear}`;
    }

    if (startYear === endYear) {
        return `${start} - ${end} ${startYear}`;
    }

    return `${start} ${startYear} - ${end} ${endYear}`;
}


export const ReshuffleRequestStatus = {
  Draft: 0,
  PendingApproval: 1,
  Approved: 2,
  Rejected: 3,
  Executed: 4,
  Cancelled: 5,
};

export const getReshuffleStatusLabel = (status) => {
  switch (status) {
    case 0: return "Draft";
    case 1: return "Pending Approval";
    case 2: return "Approved";
    case 3: return "Rejected";
    case 4: return "Executed";
    case 5: return "Cancelled";
    default: return "Unknown";
  }
};

export const getReshuffleStatusColorClass = (status) => {
  switch (status) {
    case 0: return "OnHold";      // gray
    case 1: return "OffTrack";    // amber
    case 2: return "Completed";   // blue
    case 3: return "AtRisk";      // red
    case 4: return "OnTrack";     // green
    case 5: return "Dark";        // dark gray
    default: return "OnHold";
  }
};
export function sanitizeHtml(dirty) {
  return DOMPurify.sanitize(dirty, {
    USE_PROFILES: { html: true },
  });
}

export const processDescription = (html) => {
  return html?.replace(/<p>/g, '<p class="truncate-text">');
};

export const getEditPermission = (type, selectedItem) => {
  const permissions = JSON.parse(localStorage.getItem("permissions"));
  if (permissions) {
    switch (type) {
      case "Role":
        return permissions.includes("Role.Edit") && !selectedItem.isDefault;
      case "Resource":
        return permissions.includes("User.Edit") ? true : false;
      case "Shift":
        return permissions.includes("Shift.Edit") ? true : false;
      case "Allocated Resources":
        return true;
      case "Client":
        return permissions.includes("Client.Edit") ? true : false;
      case "Portfolio":
        return permissions.includes("Portfolio.Edit") ? true : false;
      case "Program":
        return permissions.includes("Program.Edit") ? true : false;
      case "Project":
        return permissions.includes("Project.Edit") ? true : false;
      case "Scope":
        return permissions.includes("Scope.Edit") ? true : false;
      case "Tasks":
        return permissions.includes("Task.Edit") ? true : false;
      case "Change Request":
        return permissions.includes("ChangeRequest.Edit") ? true : false;
      case "Risk":
        return permissions.includes("Risk.Edit") ? true : false;
      case "Communication Plan":
        return permissions.includes("CommunicationPlan.Edit") ? true : false;
      case "Budget":
        return permissions.includes("CostItem.Edit") ? true : false;
      case "TimeOff":
        return permissions.includes("Leave.Edit") ? true : false;
      case "PurchaseOrders":
        return permissions.includes("PurchaseOrder.Edit") ? true : false;
      case "Decision":
        return permissions.includes("Decision.Edit") ? true : false;
      case "Runbook":
        return permissions.includes("Runbook.Edit") ? true : false;
      case "ReshuffleRequest":
        return permissions.includes("Project.Edit") ? true : false;
      default:
        return "";
    }
  }
};

export const getDeletePermission = (type, selectedItem) => {
  const permissions = JSON.parse(localStorage.getItem("permissions"));
  if (permissions) {
    switch (type) {
      case "Role":
        return permissions.includes("Role.Delete") && !selectedItem.isDefault
          ? true
          : false;
      case "Resource":
        return permissions.includes("User.Delete") ? true : false;
      case "Shift":
        return permissions.includes("Shift.Delete") && !selectedItem.isDefault
          ? true
          : false;
      case "Allocated Resources":
        return true;
      case "Client":
        return (
          permissions.includes("Client.Delete") && !selectedItem.isPendingDelete
        );
      case "Portfolio":
        return (
          permissions.includes("Portfolio.Delete") &&
          !selectedItem.isPendingDelete
        );
      case "Program":
        return (
          permissions.includes("Program.Delete") &&
          !selectedItem.isPendingDelete
        );
      case "Project":
        return (
          permissions.includes("Project.Delete") &&
          !selectedItem.isPendingDelete
        );
      case "Scope":
        return permissions.includes("Scope.Delete") ? true : false;
      case "Tasks":
        return permissions.includes("Task.Delete") ? true : false;
      case "Change Request":
        return permissions.includes("ChangeRequest.Delete") ? true : false;
      case "Risk":
        return permissions.includes("Risk.Delete") ? true : false;
      case "Communication Plan":
        return permissions.includes("CommunicationPlan.Delete") ? true : false;
      case "Budget":
        return permissions.includes("CostItem.Delete") ? true : false;
      case "PurchaseOrders":
        return permissions.includes("PurchaseOrder.Delete") ? true : false;
      case "Decision":
        return permissions.includes("Decision.Delete") ? true : false;
      case "Runbook":
        return permissions.includes("Runbook.Delete") ? true : false;
      case "ReshuffleRequest":
        return permissions.includes("Project.Delete") ? true : false;
      default:
        return "";
    }
  }
};

export const getCreatePermission = (type) => {
  const permissions = JSON.parse(localStorage.getItem("permissions"));
  if (permissions) {
    switch (type.toLowerCase()) {
      case "scope":
        return permissions.includes("Scope.Create") ? true : false;
      case "task":
        return permissions.includes("Task.Create") ? true : false;
      case "runbook":
        return permissions.includes("Runbook.Create") ? true : false;
      default:
        return "";
    }
  }
}

export const getViewPermission = (type) => {
  const permissions = JSON.parse(localStorage.getItem("permissions"));
  if (permissions) {
    switch (type.toLowerCase()) {
      case "task":
        return permissions.includes("Task.View") ? true : false;
      case "runbook":
        return permissions.includes("Runbook.View") ? true : false;
      default:
        return "";
    }
  }
}
// takes date in UTC iso Format and repleace its time with provided time, no conversion to local
export const parseDateTime = (date, time) => {
  if (!date || !time) return null;
  const datePart = date.split("T")[0];
  const combined = `${datePart}T${time}:00Z`;
  return combined;
};

export const convertToISOString = (dateString, timeString) => {
  // Create a Date object from the provided date string
  const date = new Date(dateString);

  // If timeString is provided and not empty, set hours and minutes
  if (timeString && timeString.trim() !== "") {
    const [hours, minutes] = timeString.split(":");
    date.setHours(parseInt(hours, 10));
    date.setMinutes(parseInt(minutes, 10));
  }

  // Use toISOString() method to convert the date to ISO 8601 format
  const isoString = date.toISOString();

  return isoString;
};

export const formatDateString = (dateString, dateFormat) => {
  const date = new Date(dateString);

  switch (dateFormat) {
    case "yyyy/MM/dd":
      return `${date.getFullYear()}/${String(date.getMonth() + 1).padStart(
        2,
        "0"
      )}/${String(date.getDate()).padStart(2, "0")}`;
    case "dd/MM/yyyy":
      return `${String(date.getDate()).padStart(2, "0")}/${String(
        date.getMonth() + 1
      ).padStart(2, "0")}/${date.getFullYear()}`;
    case "MM/dd/yyyy":
      return `${String(date.getMonth() + 1).padStart(2, "0")}/${String(
        date.getDate()
      ).padStart(2, "0")}/${date.getFullYear()}`;
    default:
      return date.toLocaleDateString();
  }
};

export const formatTime = (dateString, timeFormat) => {
  const date = new Date(dateString);

  if (timeFormat === "hh:mm") {
    // 24-hour format
    return `${String(date.getHours()).padStart(2, "0")}:${String(
      date.getMinutes()
    ).padStart(2, "0")}`;
  } else {
    // 12-hour format (assuming this is what tConverter does)
    const hours = date.getHours();
    const minutes = date.getMinutes();
    const ampm = hours >= 12 ? "PM" : "AM";
    const displayHours = hours % 12 || 12;
    return `${displayHours}:${String(minutes).padStart(2, "0")} ${ampm}`;
  }
};

export const getAllColumnIdsByOrder = (columns) => {
  return columns
    .sort((a, b) => a.order - b.order)
    .filter((col) => col.isVisible)
    .map((col) => col.accessorKey);
};

export const compareOrderLists = (newOrderList, oldOrderList) => {
  const arraysEqual =
    JSON.stringify(newOrderList) === JSON.stringify(oldOrderList);
  if (arraysEqual) {
    return null;
  }
  return newOrderList;
};

export const mapNewOrderWithColumns = (existingColumns, newOrder) => {
  const columnMap = {};
  existingColumns.forEach((col) => {
    columnMap[col.columnId] = { ...col };
  });

  const visibleOrderedColumns = [];
  newOrder.forEach((columnId, index) => {
    if (columnMap[columnId] && columnMap[columnId].isVisible) {
      visibleOrderedColumns.push({
        ...columnMap[columnId],
        order: index,
      });
    }
  });

  const invisibleColumns = [];
  existingColumns.forEach((col) => {
    if (!col.isVisible) {
      invisibleColumns.push({ ...col });
    }
  });

  let nextOrder = visibleOrderedColumns.length;
  invisibleColumns.forEach((col) => {
    col.order = nextOrder++;
  });

  const finalColumns = [...visibleOrderedColumns, ...invisibleColumns];
  finalColumns.sort((a, b) => a.order - b.order);
  return finalColumns;
};

export const compareArrays = (arr1, arr2) => {
  if (arr1.length !== arr2.length) return false;
  return arr1.every((item, index) => item === arr2[index]);
};

export const sortColumnsByOrderlist = (columns, orderlist) => {
  return columns
    .sort((a, b) => {
      const aIndex = orderlist.indexOf(a.accessorKey);
      const bIndex = orderlist.indexOf(b.accessorKey);

      if (aIndex !== -1 && bIndex !== -1) {
        return aIndex - bIndex;
      }

      if (aIndex !== -1) return -1;
      if (bIndex !== -1) return 1;

      return 0;
    })
    .map((col, index) => ({
      ...col,
      order: index,
    }));
};

export const getHealthStatusColor = (status) => {
  switch (status) {
    case 1:
      return "#EAA300";
    case 2:
      return "#C50F1F";
    default:
      return "#0B6A0B";
  }
};

export const renderHealthStatus = (status, index) => (
  <Tooltip
    key={index}
    content={`${status.period} ${status.description ? `: ${status.description}` : ""
      }`}
  >
    {status.isForecast ? (
      status.status === 0 ? (
        <svg
          width="20"
          height="18"
          viewBox="0 0 20 20"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M11.0371 1.43492C10.7621 1.15643 10.3892 1 10.0005 1C9.61176 1 9.23893 1.15643 8.96388 1.43492L3.42978 7.04242C3.1546 7.3214 3 7.69978 3 8.09432C3 8.48886 3.1546 8.86724 3.42978 9.14622C3.70496 9.42521 4.07819 9.58194 4.46736 9.58194C4.85653 9.58194 5.22976 9.42521 5.50495 9.14623L8.53359 6.07677L8.53359 17.5129C8.53359 17.9073 8.68814 18.2855 8.96324 18.5644C9.23833 18.8433 9.61144 19 10.0005 19C10.3895 19 10.7626 18.8433 11.0377 18.5644C11.3128 18.2855 11.4674 17.9073 11.4674 17.5129L11.4674 6.07677L14.4951 9.14623C14.6313 9.28436 14.7931 9.39394 14.9711 9.4687C15.1491 9.54346 15.3399 9.58194 15.5326 9.58194C15.7253 9.58194 15.9161 9.54346 16.0942 9.4687C16.2722 9.39394 16.434 9.28436 16.5702 9.14623C16.7065 9.00809 16.8146 8.84409 16.8883 8.66361C16.962 8.48312 17 8.28968 17 8.09432C17 7.89897 16.962 7.70552 16.8883 7.52504C16.8146 7.34455 16.7065 7.18056 16.5702 7.04242L11.0371 1.43492Z"
            fill="#0B6A0B"
          />
        </svg>
      ) : status.status === 1 ? (
        <svg
          width="18"
          height="18"
          viewBox="0 0 18 14"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M17.5651 8.0371C17.8436 7.76205 18 7.38922 18 7.00049C18 6.61176 17.8436 6.23893 17.5651 5.96388L11.9576 0.429781C11.6786 0.154597 11.3002 -2.96957e-07 10.9057 -3.10103e-07C10.5111 -3.23248e-07 10.1328 0.154597 9.85378 0.429781C9.57479 0.704965 9.41806 1.07819 9.41806 1.46736C9.41806 1.85653 9.57479 2.22976 9.85377 2.50495L12.9232 5.53359L1.48714 5.53359C1.09273 5.53359 0.714466 5.68814 0.435573 5.96324C0.156681 6.23833 -2.88995e-07 6.61144 -3.06001e-07 7.00049C-3.23007e-07 7.38953 0.156681 7.76264 0.435573 8.03774C0.714466 8.31284 1.09273 8.46738 1.48714 8.46738L12.9232 8.46738L9.85377 11.4951C9.71564 11.6313 9.60606 11.7931 9.5313 11.9711C9.45654 12.1491 9.41806 12.3399 9.41806 12.5326C9.41806 12.7253 9.45654 12.9161 9.5313 13.0942C9.60606 13.2722 9.71564 13.434 9.85377 13.5702C9.99191 13.7065 10.1559 13.8146 10.3364 13.8883C10.5169 13.962 10.7103 14 10.9057 14C11.101 14 11.2945 13.962 11.475 13.8883C11.6554 13.8146 11.8194 13.7065 11.9576 13.5702L17.5651 8.0371Z"
            fill="#EAA300"
          />
        </svg>
      ) : (
        <svg
          width="14"
          height="18"
          viewBox="0 0 14 18"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M5.9629 17.5651C6.23795 17.8436 6.61078 18 6.99951 18C7.38824 18 7.76107 17.8436 8.03612 17.5651L13.5702 11.9576C13.8454 11.6786 14 11.3002 14 10.9057C14 10.5111 13.8454 10.1328 13.5702 9.85378C13.295 9.57479 12.9218 9.41806 12.5326 9.41806C12.1435 9.41806 11.7702 9.57479 11.4951 9.85377L8.46641 12.9232L8.46641 1.48714C8.46641 1.09272 8.31186 0.714466 8.03676 0.435573C7.76167 0.156681 7.38856 -2.88995e-07 6.99951 -3.06001e-07C6.61047 -3.23007e-07 6.23736 0.156681 5.96226 0.435573C5.68716 0.714466 5.53262 1.09272 5.53262 1.48714L5.53262 12.9232L2.50495 9.85377C2.36869 9.71564 2.20693 9.60606 2.0289 9.5313C1.85087 9.45654 1.66006 9.41806 1.46736 9.41806C1.27467 9.41806 1.08386 9.45654 0.905829 9.5313C0.7278 9.60606 0.566038 9.71564 0.42978 9.85377C0.293523 9.99191 0.185439 10.1559 0.111698 10.3364C0.0379556 10.5169 3.18642e-07 10.7103 3.10103e-07 10.9057C3.01563e-07 11.101 0.0379556 11.2945 0.111697 11.475C0.185439 11.6554 0.293523 11.8194 0.42978 11.9576L5.9629 17.5651Z"
            fill="#C50F1F"
          />
        </svg>
      )
    ) : (
      <svg
        width="12"
        height="18"
        viewBox="0 0 12 12"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <circle cx="6" cy="6" r="6" fill={getHealthStatusColor(status.status)} />
      </svg>
    )}
  </Tooltip>
);

export const formatPhoneNumberForDisplay = (phone) => {
  const cleaned = phone.replace(/\D/g, '');
  const withPlus = cleaned.startsWith('0') ? cleaned : `+${cleaned}`;

  try {
    const phoneNumber = parsePhoneNumberFromString(withPlus);
    return phoneNumber ? phoneNumber.formatInternational() : phone;
  } catch {
    return phone;
  }
}

export const formatPhoneForTelHref = (phone) => {
  try {
    const cleaned = phone.replace(/\D/g, '');
    const number = parsePhoneNumberFromString('+' + cleaned);
    return number ? `tel:${number.number}` : `tel:${cleaned}`;
  } catch {
    return `tel:${phone}`;
  }
}

export const NotificationType = {
  Notification: 0,
  Reminder: 1,
};

export const REDIRECTION_TYPES = {
  CHANGE_REQUEST: 0,
  COMMUNICATION: 1,
  BUDGET: 2,
  RISK: 3,
  SCOPE: 4,
  TASK: 5,
};

export const TAB_MAPPING = {
  [REDIRECTION_TYPES.CHANGE_REQUEST]: 'changeRequest',
  [REDIRECTION_TYPES.COMMUNICATION]: 'communication',
  [REDIRECTION_TYPES.BUDGET]: 'budget',
  [REDIRECTION_TYPES.RISK]: 'risk/Issues',
  [REDIRECTION_TYPES.SCOPE]: 'scope',
  [REDIRECTION_TYPES.TASK]: 'task',
};

export const RoleType = {
  Custom: 0, //CST
  Administrator: 1,  //ADM
  PortfolioManager: 2,   //POM
  ProgramManager: 3,     //PRG
  ProjectManager: 4,
};

export const UserSearchRefType = {
  Client: 0,
  Project: 1,
  Scope: 2,
};

// Runbook enums
export const RunbookStatus = {
  Draft: 0,
  Ready: 1,
  InProgress: 2,
  Completed: 3,
  Aborted: 4,
};

export const RunbookActionStatus = {
  Pending: 0,
  InProgress: 1,
  Completed: 2,
  Failed: 3,
};

export const getRunbookStatusLabel = (status) => {
  switch (status) {
    case RunbookStatus.Draft: return "Draft";
    case RunbookStatus.Ready: return "Ready";
    case RunbookStatus.InProgress: return "In Progress";
    case RunbookStatus.Completed: return "Completed";
    case RunbookStatus.Aborted: return "Aborted";
    default: return "";
  }
};

export const getRunbookStatusClass = (status) => {
  switch (status) {
    case RunbookStatus.Draft: return "pill runbook-draft";
    case RunbookStatus.Ready: return "pill runbook-ready";
    case RunbookStatus.InProgress: return "pill runbook-inprogress";
    case RunbookStatus.Completed: return "pill runbook-completed";
    case RunbookStatus.Aborted: return "pill runbook-aborted";
    default: return "";
  }
};

export const getRunbookActionStatusLabel = (status) => {
  switch (status) {
    case RunbookActionStatus.Pending: return "Pending";
    case RunbookActionStatus.InProgress: return "In Progress";
    case RunbookActionStatus.Completed: return "Completed";
    case RunbookActionStatus.Failed: return "Failed";
    default: return "";
  }
};

export const getRunbookActionStatusClass = (status) => {
  switch (status) {
    case RunbookActionStatus.Pending: return "pill runbook-action-pending";
    case RunbookActionStatus.InProgress: return "pill runbook-action-inprogress";
    case RunbookActionStatus.Completed: return "pill runbook-action-completed";
    case RunbookActionStatus.Failed: return "pill runbook-action-failed";
    default: return "";
  }
};

export const formatDuration = (minutes) => {
  if (!minutes && minutes !== 0) return `0m`;
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  if (h > 0 && m > 0) return `${h}h ${m}m`;
  if (h > 0) return `${h}h`;
  return `${m}m`;
};

export const formatElapsed = (ms) => {
  const totalSeconds = Math.floor(ms / 1000);
  const h = Math.floor(totalSeconds / 3600);
  const min = Math.floor((totalSeconds % 3600) / 60);
  const sec = totalSeconds % 60;
  return [h, min, sec].map((v) => String(v).padStart(2, "0")).join(":");
};
